import React, { useState, useEffect, useCallback } from "react";
import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import './App.css';
import CompanyForm from "./components/CompanyForm";
import MyAccount from "./components/MyAccount";
import Contacts from "./components/Contacts";
import UsersAdmin from "./components/UsersAdmin";
import ContactManagement from "./components/ContactManagement";
import NewContactsButton from "./components/NewContactsButton";
import LeadyModal from "./components/LeadyModal";
import Dashboard from "./components/Dashboard";
import CommissionsWithTabs from "./components/CommissionsWithTabs";
import MeetingPlanner from "./components/MeetingPlanner";
import Activities from "./components/Activities";
import Requests from "./components/Requests";
import Chat from "./components/Chat";
import VyjednameLogo from "./components/VyjednameLogo";
import NotificationBell from "./components/NotificationBell";
import PhoneSync from "./components/PhoneSync";
import MobilePhoneSync from "./components/MobilePhoneSync";
import DynamicGrid from "./components/DynamicGrid";
import StatusEditor from "./components/StatusEditor";
import BatchVisibilityManager from "./components/BatchVisibilityManager";
import UserStatsWidget, { DuelBar } from "./components/UserStatsWidget";
import { exportToCSV } from "./utils/export";
import { getCompanyStatuses } from "./utils/statuses";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { useContextMenu } from "./components/ContextMenu";
import BackupRestore from "./components/BackupRestore";
import RestoreFromBackup from "./components/RestoreFromBackup";
import MindMapKnowledgeManager from "./components/MindMapKnowledgeManager";
import CallCenterAdmin from "./components/CallCenterAdmin";
import CallCenterStatsModal from "./components/CallCenterStatsModal";
import DiscordCallback from "./components/DiscordCallback";
import BulkAssignment from "./components/BulkAssignment";
import DuplicateChecker from "./components/DuplicateChecker";
import DailyMessagePopup from "./components/DailyMessagePopup";
import WelcomeAdmin from "./components/WelcomeAdmin";
import { API_URL } from "./config";
import CompanyCounter from "./components/CompanyCounter";
import { useRolePermissions } from './hooks/useRolePermissions';
import SuperAdminConsole from "./components/SuperAdminConsole";
import SuppliersAdmin from "./components/SuppliersAdmin";
import SuperuseryAdmin from "./components/SuperuseryAdmin";
import UvolneniMustrAdmin from "./components/UvolneniMustrAdmin";
import PrmUpdateModal from "./components/PrmUpdateModal";
import BackofficeAdmin from "./components/BackofficeAdmin";
import LeadBaAdmin from "./components/LeadBaAdmin";
import LeadtoolLogin from "./pages/LeadtoolLogin";
import LeadtoolRegister from "./pages/LeadtoolRegister";
import LeadtoolDashboard from "./pages/LeadtoolDashboard";
import CloudStorage from "./components/CloudStorage";
import Sprava from "./components/Sprava";
import { VerificationProvider } from './context/VerificationContext';
import VerificationStatusBar from './components/VerificationStatusBar';

// Pomocná funkce pro porovnání dnů
function isSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// Stránka pro přidání nové firmy
function NewCompanyPage({ users, handleSaveNewCompany, isDarkMode, companies, userRole, fetchCompanies, userId, onOpenMeetingPlanner }) {
  const navigate = useNavigate();

  const handleSave = async (form) => {
    const savedCompany = await handleSaveNewCompany(form);
    if (savedCompany) {
      // Po úspěšném uložení přejdi na detail firmy
      navigate(`/companies/${savedCompany._id}`);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${isDarkMode ? 'from-gray-900 to-gray-800' : 'from-gray-100 to-blue-100'}`}>
      <div className="w-full max-w-[1800px] mx-auto py-10 px-2">
        <CompanyForm
          company={null}
          users={users}
          onSave={handleSave}
          onCancel={handleCancel}
          isEdit={true}
          isDarkMode={isDarkMode}
          companies={companies}
          userRole={userRole}
          fetchCompanies={fetchCompanies}
          userId={userId}
          onOpenMeetingPlanner={onOpenMeetingPlanner}
        />
      </div>
    </div>
  );
}

// Detail firmy jako stránka
function CompanyDetailPage({ companies, fetchCompanies, userId, isDarkMode, userRole, onOpenMeetingPlanner, filteredCompanies, users, setCurrentOpenCompanyId }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Pokud máme firmy v cache, zkus najít tam
        const cachedCompany = companies.find(c => c._id === id);
        if (cachedCompany) {
          setCompany(cachedCompany);
          setLoading(false);
        }
        
        // Načti z API pro jistotu
        const res = await fetch(`${API_URL}/companies/${id}?userId=${userId || ''}`);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        setCompany(data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching company detail:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    if (id) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, companies]);

  // Samostatný efekt pro sync s mobilem - závisí POUZE na id (ne na companies!)
  useEffect(() => {
    if (!id || !setCurrentOpenCompanyId) return;
    setCurrentOpenCompanyId(id);
    return () => setCurrentOpenCompanyId(null);
  }, [id, setCurrentOpenCompanyId]);

  const handleSave = async (form) => {
    try {
      const transferMeta = form?._transferMeta || null;
      const payload = { ...form };
      if (payload._transferMeta) {
        delete payload._transferMeta;
      }

      const headers = { "Content-Type": "application/json" };
      if (transferMeta?.initiatedBy) {
        headers["X-User-Id"] = String(transferMeta.initiatedBy);
      }
      if (transferMeta?.transferType) {
        headers["X-Transfer-Type"] = String(transferMeta.transferType);
      }
      // Pozn.: batchName se posílá v těle požadavku (payload.batchName), NE v HTTP hlavičce.
      // HTTP hlavičky povolují jen ISO-8859-1; české znaky (č, ř, ž…) by jinak shodily fetch.

      const res = await fetch(`${API_URL}/companies/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        fetchCompanies(userId);
        const updated = await res.json();
        setCompany(updated);
      } else {
        const errText = await res.text().catch(() => '');
        console.error('Uložení firmy selhalo:', res.status, errText);
        alert(`Uložení se nezdařilo (HTTP ${res.status}). ${errText || ''}`.trim());
      }
    } catch (err) {
      console.error('Error saving company:', err);
      alert('Chyba při ukládání firmy: ' + (err?.message || err));
    }
  };

  const handleAddActivity = async (text) => {
    try {
      const res = await fetch(`${API_URL}/companies/${id}/activities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, createdBy: userId }),
      });
      if (res.ok) {
        const updated = await res.json();
        setCompany(updated);
        fetchCompanies(userId);
      }
    } catch (err) {
      console.error('Error adding activity:', err);
    }
  };

  // Výpočet nextCompanyId - použij filtrované firmy seřazené podle data další akce (stejně jako v hlavní tabulce)
  const companiesToSort = filteredCompanies && filteredCompanies.length > 0 ? filteredCompanies : companies;
  const sortedCompanies = [...companiesToSort].sort((a, b) => {
    if (!a.nextActionDate) return 1;
    if (!b.nextActionDate) return -1;
    return new Date(a.nextActionDate) - new Date(b.nextActionDate);
  });
  
  const idx = sortedCompanies.findIndex(c => c._id === id);
  const nextCompanyId = idx >= 0 && idx < sortedCompanies.length - 1 ? sortedCompanies[idx + 1]._id : null;

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} py-10 flex items-center justify-center`}>
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
          <div className="animate-pulse text-center">
            <div className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">Načítám detail firmy...</div>
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} py-10 flex items-center justify-center`}>
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="text-xl font-bold text-red-600 mb-4">❌ Chyba při načítání</div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded font-semibold transition"
              >
                🔄 Zkusit znovu
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded font-semibold transition"
              >
                ← Zpět na hlavní stránku
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} py-10 flex items-center justify-center`}>
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
          <div className="text-center">
            <div className="text-xl font-bold text-yellow-600 mb-4">⚠️ Firma nenalezena</div>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded font-semibold transition"
            >
              ← Zpět na hlavní stránku
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isDarkMode ? 'dark' : ''}`}>
      <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} pb-10`}>
        <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
          <CompanyForm
            company={company}
            users={users}
            onSave={handleSave}
            onAddActivity={handleAddActivity}
            nextCompanyId={nextCompanyId}
            isDarkMode={isDarkMode}
            companies={companies}
            userRole={userRole}
            fetchCompanies={fetchCompanies}
            userId={userId}
            onOpenMeetingPlanner={onOpenMeetingPlanner}
          />
        </div>
      </div>
    </div>
  );
}

function MainApp() {
  const isIOSPWA = window.navigator.standalone === true;
  const navigate = useNavigate();
  const location = useLocation();
  const isMainPage = location.pathname === '/';
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [show2FAScreen, setShow2FAScreen] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFATrustDevice, setTwoFATrustDevice] = useState(false);
  const [twoFAEmailFailed, setTwoFAEmailFailed] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [tenantLogo, setTenantLogo] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [search, setSearch] = useState("");
  const [showMeetingPlanner, setShowMeetingPlanner] = useState(false);
  const [dailyPopup, setDailyPopup] = useState({ show: false, message: "" });
  const [selectedCustomerForPlanner, setSelectedCustomerForPlanner] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [showCompleted, setShowCompleted] = useState(false);
  const [showNotInterested, setShowNotInterested] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null); // Pro uchovávání celého user objektu s teamleaderFor
  const [showTeamleaderDropdown, setShowTeamleaderDropdown] = useState(false);
  const [originalUserId, setOriginalUserId] = useState(null); // Pro uchování původního ID při switchu
  const [showOverdue, setShowOverdue] = useState(() => localStorage.getItem('filterShowOverdue') === 'true');
  const [showToday, setShowToday] = useState(() => localStorage.getItem('filterShowToday') === 'true');
  const [showCallCenterStats, setShowCallCenterStats] = useState(false);
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [activityDateFilter, setActivityDateFilter] = useState({ from: "", to: "" });
  const [bmslMin, setBmslMin] = useState(""); // Filtr BMSL - vyšší než
  const [bmslMax, setBmslMax] = useState(""); // Filtr BMSL - nižší než
  const [operatorFilter, setOperatorFilter] = useState([]); // Filtr operátorů (checkbox)
  const [prmFilter, setPrmFilter] = useState(""); // Filtr PRM (text)
  const [leadFilter, setLeadFilter] = useState(false); // Filtr pouze leady (z callcentra)
  const [mainSortOrder, setMainSortOrder] = useState('date'); // 'date' | 'bmsl_asc' | 'bmsl_desc'
  const [filterTemplates, setFilterTemplates] = useState([]); // Šablony filtrů
  const [activeTemplateId, setActiveTemplateId] = useState(() => localStorage.getItem('activeFilterTemplateId') || null); // Aktivní šablona
  const [showSaveTemplateInput, setShowSaveTemplateInput] = useState(false); // Zobrazit input pro uložení šablony
  const [newTemplateName, setNewTemplateName] = useState(''); // Název nové šablony
  const [statusSearch, setStatusSearch] = useState(""); // Vyhledávání ve stavech
  const [statusFilter, setStatusFilter] = useState([]); // Změna na pole pro více stavů
  const [forecastFilter, setForecastFilter] = useState([]); // Nový filtr pro forecast
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [customFilteredCompanies, setCustomFilteredCompanies] = useState([]);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false); // Sjednocený panel filtrů
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false); // Loading state pro načítání firem
  const [selectedActivityDay, setSelectedActivityDay] = useState(() => {
    const saved = localStorage.getItem('homepage_selected_activity_day');
    return saved || null;
  });
  
  // Stav pro nastavení grafů
  const [chartSettings, setChartSettings] = useState(null);
  // Koeficient pro výpočet očekávané provize – načítá se ze serveru, fallback z localStorage nebo default 4/6
  const _localCoef = (() => { try { return JSON.parse(localStorage.getItem('commissionCoef')); } catch { return null; } })();
  const [commissionCoef, setCommissionCoef] = useState(_localCoef || { coefMin: 4, coefMax: 6 });
  
  // Stavy pro mobilní menu - přesunuté z DashboardLayout pro prevenci re-renderování
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [showLeadyModal, setShowLeadyModal] = useState(false);
  const [showPrmUpdateModal, setShowPrmUpdateModal] = useState(false);
  const [showPreblokaceModal, setShowPreblokaceModal] = useState(false);
  
  // Uložené filtry pro obnovení po vymazání vyhledávání
  const [savedFilters, setSavedFilters] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  
  // Stavy pro globální vyhledávání (admin only)
  const [isGlobalSearch, setIsGlobalSearch] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isSearchingGlobally, setIsSearchingGlobally] = useState(false);
  
  // Stavy z databáze pro konfiguraci propadlých aktivit
  const [availableStatuses, setAvailableStatuses] = useState([]);
  
  // Stav pro tracking otevřené firmy (pro synchronizaci s mobilem)
  const [currentOpenCompanyId, setCurrentOpenCompanyId] = useState(null);

  // Role permissions hook
  const { hasPermission } = useRolePermissions(userRole);

  // Context menu hook
  const { isVisible: contextMenuVisible, position: contextMenuPosition, items: contextMenuItems, showContextMenu, hideContextMenu } = useContextMenu();

  // useCallback pro search handler s debouncing pro currentPage
  const handleSearchChange = useCallback((newValue) => {
    const isNewValueEmpty = !newValue || newValue.trim() === "";
    
    // Pokud se začíná vyhledávat a filtry ještě nejsou uložené
    if (!isNewValueEmpty && !isSearchActive) {
      // Ulož současné filtry
      setSavedFilters({
        showCompleted,
        showNotInterested,
        showOverdue,
        showToday,
        dateFilter,
        activityDateFilter,
        bmslMin,
        bmslMax,
        statusFilter,
        forecastFilter,
        operatorFilter,
        prmFilter,
        leadFilter
      });
      
      // Vypni všechny filtry
      setShowCompleted(false);
      setShowNotInterested(false);
      setShowOverdue(false);
      setShowToday(false);
      setDateFilter({ from: "", to: "" });
      setActivityDateFilter({ from: "", to: "" });
      setBmslMin("");
      setBmslMax("");
      setStatusFilter([]);
      setForecastFilter([]);
      setOperatorFilter([]);
      setPrmFilter("");
      setLeadFilter(false);
      setIsSearchActive(true);
    }
    
    // Pokud se maže vyhledávání a existují uložené filtry
    if (isNewValueEmpty && isSearchActive && savedFilters) {
      // Obnov uložené filtry
      setShowCompleted(savedFilters.showCompleted);
      setShowNotInterested(savedFilters.showNotInterested);
      setShowOverdue(savedFilters.showOverdue);
      setShowToday(savedFilters.showToday);
      setDateFilter(savedFilters.dateFilter);
      setActivityDateFilter(savedFilters.activityDateFilter);
      setBmslMin(savedFilters.bmslMin || "");
      setBmslMax(savedFilters.bmslMax || "");
      setStatusFilter(savedFilters.statusFilter);
      setForecastFilter(savedFilters.forecastFilter);
      setOperatorFilter(savedFilters.operatorFilter || []);
      setPrmFilter(savedFilters.prmFilter || "");
      setLeadFilter(savedFilters.leadFilter || false);
      setIsSearchActive(false);
      setSavedFilters(null);
    }
    
    setSearch(newValue);
    setCurrentPage(1);
  }, [isSearchActive, savedFilters, showCompleted, showNotInterested, showOverdue, showToday, dateFilter, activityDateFilter, bmslMin, bmslMax, statusFilter, forecastFilter, operatorFilter, prmFilter]);

  // Vyčištění search filtru při obnově stránky
  useEffect(() => {
    setSearch("");
  }, []); // Prázdný dependency array znamená, že se spustí pouze při mount

  // Načtení nastavení grafů
  useEffect(() => {
    if (!userId) return;
    const loadChartSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/chart-settings?userId=${userId}`);
        if (response.ok) {
          const data = await response.json();
          setChartSettings(data);
        }
      } catch (error) {
        console.error('Chyba při načítání nastavení grafů:', error);
      }
    };
    loadChartSettings();
  }, [userId]);

  // Načtení koeficientu pro očekávanou provizi
  useEffect(() => {
    if (!userId) return;
    fetch(`${API_URL}/commission-coef?userId=${userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setCommissionCoef(data);
          localStorage.setItem('commissionCoef', JSON.stringify(data));
        }
      })
      .catch(() => {});
  }, [userId]);

  // Dynamická změna theme-color pro iOS podle tmavého režimu
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDarkMode ? '#111827' : '#1d4ed8');
    }
  }, [isDarkMode]);

  // Načítání stavů z databáze pro konfiguraci propadlých aktivit
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const statuses = await getCompanyStatuses();
        setAvailableStatuses(statuses);
      } catch (error) {
        console.error('Chyba při načítání stavů:', error);
        setAvailableStatuses([]);
      }
    };
    
    loadStatuses();
  }, []);

  // Helper funkce pro filtrování firem
  const getFilteredCompanies = (companies, searchTerm, filters) => {
    return companies.filter(company => {
      const q = searchTerm.toLowerCase();
      const qNormPhone = normalizePhone(searchTerm);

      // Hledání v základních polích firmy
      const baseMatch = q === "" || 
        (company.name && company.name.toLowerCase().includes(q)) ||
        (company.ico && company.ico.toLowerCase().includes(q)) ||
        (company.address && company.address.toLowerCase().includes(q)) ||
        (company.status && company.status.toLowerCase().includes(q)) ||
        (company.activity && company.activity.toLowerCase().includes(q)) ||
        (company.note && company.note.toLowerCase().includes(q));

      // Vylepšené hledání v kontaktech s normalizací telefonu
      const contactsMatch = q === "" || (company.contacts || []).some(contact => {
        const nameMatch = contact.name && contact.name.toLowerCase().includes(q);
        const emailMatch = contact.email && contact.email.toLowerCase().includes(q);
        
        // Hledání v telefonu - porovnává jak původní číslo, tak normalizované
        // Pokryje formáty: 777222111, +420777222111, +420 777 222 111, 777 222 111...
        const phoneMatch = contact.phone && (
          contact.phone.toLowerCase().includes(q) || 
          (qNormPhone.length >= 6 && normalizePhone(contact.phone).includes(qNormPhone))
        );
        
        return nameMatch || emailMatch || phoneMatch;
      });

      // Hledání v aktivitách firmy (text aktivity)
      const activitiesMatch = q === "" || (company.activities || []).some(activity => {
        return activity.text && activity.text.toLowerCase().includes(q);
      });

      // Filtrování podle stavů
      let statusMatch = true;
      
      // Filtr podle vybraných konkrétních stavů
      if (filters.statusFilter.length > 0) {
        statusMatch = filters.statusFilter.includes(company.status);
      }
      
      // Filtr pro propadlé
      if (filters.showOverdue) {
        let shouldIncludeInOverdue = true;
        
        // Najdi stav v databázi stavů a zkontroluj, zda není vyloučený
        const statusInDb = availableStatuses.find(status => status.name === company.status);
        if (statusInDb && statusInDb.excludeFromOverdue) {
          shouldIncludeInOverdue = false;
        }
        
        // Fallback pro stavy, které nejsou v databázi
        if (!statusInDb) {
          const negativeStatuses = [
            'Hotovo', 'Nemá zájem', 'Odmítnut', 'Odmítnuto',
            'Ukončen', 'Ukončeno', 'Stornován', 'Stornováno',
            'Zrušen', 'Zrušeno'
          ];
          
          if (negativeStatuses.includes(company.status)) {
            shouldIncludeInOverdue = false;
          }
        }
        
        statusMatch = statusMatch && isOverdue(company.nextActionDate) && shouldIncludeInOverdue;
      }
      
      // Filtr pro dnešní aktivity
      if (filters.showToday) {
        statusMatch = statusMatch && isToday(company.nextActionDate);
      }

      // Filtr podle vybraného dne z grafu aktivit
      if (filters.selectedActivityDay) {
        const selectedDate = new Date(filters.selectedActivityDay);
        const actionDate = company.nextActionDate ? new Date(company.nextActionDate) : null;
        statusMatch = statusMatch && actionDate && isSameDay(selectedDate, actionDate);
      }

      // Filtrování podle data vytvoření
      let createdMatch = true;
      if (filters.dateFilter.from || filters.dateFilter.to) {
        const createdDate = new Date(company.createdAt || company._id);
        if (filters.dateFilter.from) {
          createdMatch = createdMatch && createdDate >= new Date(filters.dateFilter.from);
        }
        if (filters.dateFilter.to) {
          const toDate = new Date(filters.dateFilter.to);
          toDate.setHours(23, 59, 59, 999);
          createdMatch = createdMatch && createdDate <= toDate;
        }
      }

      // Filtrování podle BMSL (rozsah)
      let bmslMatch = true;
      const bmslVal = parseFloat(company.bmsl);
      if (filters.bmslMin !== "") {
        bmslMatch = bmslMatch && !isNaN(bmslVal) && bmslVal >= parseFloat(filters.bmslMin);
      }
      if (filters.bmslMax !== "") {
        bmslMatch = bmslMatch && !isNaN(bmslVal) && bmslVal <= parseFloat(filters.bmslMax);
      }

      // Filtrování podle forecastu
      let forecastMatch = true;
      if (filters.forecastFilter.length > 0) {
        forecastMatch = filters.forecastFilter.includes(company.forecast);
      }

      // Filtrování podle operátora
      let operatorMatch = true;
      if (filters.operatorFilter && filters.operatorFilter.length > 0) {
        operatorMatch = filters.operatorFilter.includes(company.operator);
      }

      // Filtrování podle PRM
      let prmMatch = true;
      if (filters.prmFilter && filters.prmFilter !== "") {
        prmMatch = company.prm && company.prm.toLowerCase().includes(filters.prmFilter.toLowerCase());
      }

      // Filtrování podle lead původu (firmy odeslané z callcentra)
      let leadMatch = true;
      if (filters.leadFilter) {
        leadMatch = company.activities?.some(a => a.text?.includes('Lead odeslán')) || false;
      }

      return (baseMatch || contactsMatch || activitiesMatch) && statusMatch && createdMatch && bmslMatch && forecastMatch && operatorMatch && prmMatch && leadMatch;
    });
  };

  // Funkce pro globální vyhledávání (admin only)
  const searchGlobally = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setIsGlobalSearch(false);
      setGlobalSearchResults([]);
      return;
    }
    
    setIsSearchingGlobally(true);
    
    try {
      console.log('🌍 Globální vyhledávání:', searchTerm.trim());
      
      // Použijeme stejný endpoint jako v ContactManagement
      const searchUrl = `${API_URL}/companies?search=${encodeURIComponent(searchTerm.trim())}&limit=100&userId=${userId || ''}&globalSearch=true`;
      console.log('🔗 URL:', searchUrl);
      
      const response = await fetch(searchUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const results = Array.isArray(data) ? data : (data.companies || []);
      
      console.log('✅ Globální vyhledávání nalezlo:', results.length, 'firem');
      
      setGlobalSearchResults(results);
      setIsGlobalSearch(true);
      
    } catch (error) {
      console.error('❌ Chyba při globálním vyhledávání:', error);
      setGlobalSearchResults([]);
      setIsGlobalSearch(false);
    } finally {
      setIsSearchingGlobally(false);
    }
  };

  // Helper funkce pro převod operátora z hodnoty na zobrazovaný text
  const getOperatorDisplayText = (operatorValue) => {
    switch (operatorValue) {
      case 'TM':
        return 'T-Mobile';
      case 'O2':
        return 'O2';
      case 'Vodafone':
        return 'Vodafone';
      default:
        return operatorValue || '';
    }
  };

  // Zobrazení denní zprávy po přihlášení (jednou za den, cross-device)
  useEffect(() => {
    if (!isLoggedIn || !userId) return;

    const todayStr = (() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    })();

    const checkAndShowPopup = async () => {
      // Nejprve rychlá kontrola v localStorage (aby nevznikal zbytečný request)
      const localKey = `dailyMsgSeen_${userId}_${todayStr}`;
      if (localStorage.getItem(localKey)) return;

      try {
        // Ověř na serveru (cross-device)
        const seenRes = await fetch(`${API_URL}/daily-messages/seen/${userId}/${todayStr}`);
        if (seenRes.ok) {
          const { seen } = await seenRes.json();
          if (seen) {
            localStorage.setItem(localKey, "1");
            return;
          }
        }

        // Načti zprávu pro dnešek
        const msgRes = await fetch(`${API_URL}/daily-messages/${todayStr}?userId=${userId}`);
        if (msgRes.ok) {
          const { message } = await msgRes.json();
          // Zobraz popup vždy (i bez zprávy – kvůli svátku), ale jen pokud je svátek nebo zpráva
          const { getNameDay } = await import("./data/czechNameDays");
          const nameDay = getNameDay(todayStr);
          if (message || nameDay) {
            setDailyPopup({ show: true, message: message || "" });
          } else {
            // Nic k zobrazení – rovnou označ jako viděno
            localStorage.setItem(localKey, "1");
          }
        }
      } catch (_) {
        // Síťová chyba – tiše ignorujeme
      }
    };

    checkAndShowPopup();
  }, [isLoggedIn, userId]);

  const handleDismissPopup = async () => {
    const todayStr = (() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    })();
    const localKey = `dailyMsgSeen_${userId}_${todayStr}`;
    localStorage.setItem(localKey, "1");
    setDailyPopup({ show: false, message: "" });
  };

  // Aktualizace lastOnline při aktivitě uživatele
  useEffect(() => {
    if (!isLoggedIn || !userId) return;

    let lastActivity = Date.now();
    let activityTimer;

    const updateLastOnline = async () => {
      try {
        // Používáme endpoint /api/users/online místo PUT /api/users/:id
        // aby se aktualizoval pouze online status, ne celý uživatelský profil
        await fetch(`${API_URL}/users/online`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: userId }),
        });
        console.log("🟢 Online status updated");
      } catch (err) {
        console.error("❌ Error updating online status:", err);
      }
    };

    const handleActivity = () => {
      lastActivity = Date.now();
    };

    // Aktualizuj každých 2 minuty pokud byl uživatel aktivní
    const startActivityTimer = () => {
      activityTimer = setInterval(() => {
        if (Date.now() - lastActivity < 5 * 60 * 1000) { // 5 minut
          updateLastOnline();
        }
      }, 2 * 60 * 1000); // každé 2 minuty (častěji než timeout 5 minut)
    };

    // Přidej event listenery pro aktivitu
    document.addEventListener('click', handleActivity);
    document.addEventListener('keypress', handleActivity);
    document.addEventListener('scroll', handleActivity);
    document.addEventListener('mousemove', handleActivity);

    // První aktualizace ihned
    updateLastOnline();
    startActivityTimer();

    return () => {
      if (activityTimer) clearInterval(activityTimer);
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('keypress', handleActivity);
      document.removeEventListener('scroll', handleActivity);
      document.removeEventListener('mousemove', handleActivity);
    };
  }, [isLoggedIn, userId]);

  // Klávesové zkratky
  useKeyboardShortcuts({
    'ctrl+n': () => navigate('/companies/new'),
    'ctrl+d': () => navigate('/dashboard'),
    'ctrl+a': () => navigate('/activities'),
    'ctrl+f': () => document.querySelector('input[placeholder*="Filtrujte"]')?.focus(),
    'ctrl+e': () => userRole === "admin" ? exportToCSV(filteredCompanies, 'export-firem') : null,
    'ctrl+shift+c': () => {
      setDateFilter({ from: "", to: "" });
      setActivityDateFilter({ from: "", to: "" });
      setSearch("");
      setShowCompleted(false);
      setShowNotInterested(false);
      setShowOverdue(false);
      setShowToday(false);
      setBmslMin("");
      setBmslMax("");
      setStatusFilter([]); // Reset filtru stavu
      setLeadFilter(false); // Reset filtru leady
    }
  });

  // Callback funkce pro otevření meeting planneru s předvybraným zákazníkem
  const handleOpenMeetingPlannerWithCustomer = useCallback((customer) => {
    setSelectedCustomerForPlanner(customer);
    setShowMeetingPlanner(true);
  }, []);

  // Callback pro zavření meeting planneru
  const handleCloseMeetingPlanner = useCallback(() => {
    setShowMeetingPlanner(false);
    setSelectedCustomerForPlanner(null);
  }, []);

  // Načti firmy z backendu po dávkách, aby velké objemy dat nespadly na jednom requestu
  const fetchCompanies = async (userId) => {
    if (!userId || userId === 'undefined') {
      setCompanies([]);
      return;
    }
    
    try {
      setIsLoadingCompanies(true);

      const CHUNK_SIZE = 10000;
      const headers = {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };

      const fetchCursor = async (afterId, afterUpdatedAt) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.warn(`⚠️ API request timeout (60s), aborting cursor fetch...`);
          controller.abort();
        }, 60000);
        try {
          let url = `${API_URL}/companies?userId=${userId}&limit=${CHUNK_SIZE}`;
          if (afterId && afterUpdatedAt) {
            url += `&afterId=${afterId}&afterUpdatedAt=${encodeURIComponent(afterUpdatedAt)}`;
          }
          const res = await fetch(url, { signal: controller.signal, headers });
          clearTimeout(timeoutId);
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          return await res.json();
        } catch (err) {
          clearTimeout(timeoutId);
          throw err;
        }
      };

      // Cursor-based pagination — žádný skip(), každá stránka je stejně rychlá
      let allCompanies = [];
      let afterId = null;
      let afterUpdatedAt = null;
      let hasMore = true;
      let pageNum = 0;

      while (hasMore) {
        const data = await fetchCursor(afterId, afterUpdatedAt);
        const pageCompanies = Array.isArray(data) ? data : (data.companies || []);
        allCompanies = allCompanies.concat(pageCompanies);

        // Zobraz průběžně
        setCompanies([...allCompanies]);

        hasMore = data.hasMore === true;
        afterId = data.nextId || null;
        afterUpdatedAt = data.nextUpdatedAt || null;
        pageNum++;
        console.log(`Načtena stránka ${pageNum}: ${pageCompanies.length} firem (celkem ${allCompanies.length})`);

        // Fallback: pokud backend vrátí starý paginační formát (pagination.hasNextPage)
        if (hasMore === false && data.pagination?.hasNextPage) {
          // Starý formát — přepadni na page-based (nemělo by nastat)
          break;
        }
      }
      
      // Načti také upselly pro tohoto uživatele
      try {
        const upsellsRes = await fetch(`${API_URL}/upsells?userId=${userId}`);
        if (upsellsRes.ok) {
          const upsellsData = await upsellsRes.json();
          setUpsells(Array.isArray(upsellsData) ? upsellsData : []);
        } else {
          setUpsells([]);
        }
      } catch (upsellErr) {
        console.error('Error fetching upsells:', upsellErr);
        setUpsells([]);
      }
      
      // Získej dostupné stavy a forecast hodnoty ze všech načtených firem
      const availableStatuses = [...new Set(allCompanies
        .map(company => company.status)
        .filter(status => status && status.trim() !== "")
      )].sort();
      
      const availableForecasts = [...new Set(allCompanies
        .map(company => company.forecast)
        .filter(forecast => forecast !== undefined && forecast !== null && forecast !== "")
      )].sort();
      
      // Načti výchozí filtry po načtení firem
      await loadDefaultFilters(userId, availableStatuses, availableForecasts);
      
    } catch (err) {
      console.error('Error fetching companies:', err);
      
      // Safari-specifická detekce stuck requestu
      if (err.name === 'AbortError') {
        console.error('🔴 Request timed out - possible Safari cache issue');
        alert('Načítání trvá příliš dlouho. Zkuste:\n1. Obnovit stránku (Cmd+R)\n2. Vymazat cache Safari\n3. Zkusit v privátním režimu');
      }
      
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    const storedName = localStorage.getItem("name");
    const storedRole = localStorage.getItem("role");
    const storedDarkMode = localStorage.getItem("darkMode");
    const storedRememberMe = localStorage.getItem("rememberMe");
    
    // Načti stav "Zapamatovat si mě"
    if (storedRememberMe === "true") {
      setRememberMe(true);
    }
    
    // Automaticky přihlas pouze pokud je zaškrtnuto "Zapamatovat si mě"
    if (storedUserId && storedName && storedRememberMe === "true") {
      // Uložíme aktuálního uživatele jako posledního
      localStorage.setItem("lastUserId", storedUserId);
      
      setUserId(storedUserId);
      setName(storedName);
      setUserRole(storedRole || "");
      setIsLoggedIn(true);
      fetchCompanies(storedUserId);
      fetchTenantLogo(storedUserId);
      // loadDefaultFilters se nyní volá v fetchCompanies po načtení firem
    }
    
    // Načti tmavý režim
    if (storedDarkMode === "true") {
      setIsDarkMode(true);
    }
    
    // Načti seznam uživatelů (s tenant filtracií)
    fetch(`${API_URL}/users?userId=${storedUserId || ''}`)
      .then(res => res.json())
      .then(data => {
        // Najdi přihlášeného uživatele pro zjištění teamleaderFor
        const loggedInUser = storedUserId ? data.find(u => u._id === storedUserId) : null;
        const teamleaderForIds = loggedInUser?.teamleaderFor || [];
        // Vyfiltruj databázové uživatele pro běžné uživatele, ale zachovej databázové uživatele přiřazené v teamleaderFor
        const filteredUsers = data.filter(user => 
          user.role !== 'database' || userRole === 'admin' || teamleaderForIds.includes(user._id)
        );
        setUsers(filteredUsers);
        
        // Nastav currentUser pokud je uživatel přihlášen
        if (loggedInUser) {
          setCurrentUser(loggedInUser);
          setOriginalUserId(storedUserId); // Uložíme původní ID
        }
      })
      .catch(() => setUsers([]));
  }, []);

  // Aplikuj tmavý režim na DOM
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Zavři teamleader dropdown při kliknutí mimo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTeamleaderDropdown && !event.target.closest('.relative')) {
        setShowTeamleaderDropdown(false);
      }
    };

    if (showTeamleaderDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTeamleaderDropdown]);

  const fetchTenantLogo = async (uid) => {
    if (!uid) return;
    try {
      const res = await fetch(`${API_URL}/tenant-logo?userId=${uid}`);
      if (res.ok) {
        const data = await res.json();
        setTenantLogo(data.logoUrl || null);
        setTenantName(data.tenantName || null);
      }
    } catch (e) {}
  };

  const handleLogin = async () => {
    if (isLoginLoading) return;
    try {
      setIsLoginLoading(true);
      console.log("Login attempt - API_URL:", API_URL);
      console.log("Login URL:", `${API_URL}/login`);
      console.log("Username:", username);

      const deviceToken = localStorage.getItem('deviceToken');
      
      const res = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: username, password, deviceToken }),
      });
      
      console.log("Response status:", res.status);
      console.log("Response ok:", res.ok);
      
      const data = await res.json();
      console.log("Response data:", data);
      
      if (res.ok) {
        // 2FA vyžadováno
        if (data.requires2FA) {
          setTwoFAUserId(data.userId);
          setTwoFACode("");
          setTwoFATrustDevice(false);
          setTwoFAEmailFailed(!!data.emailFailed);
          setShow2FAScreen(true);
          return;
        }

        // Ukládej do localStorage pouze pokud je zaškrtnut checkbox "Zapamatovat si mě"
        if (rememberMe) {
          localStorage.setItem("userId", data.user._id);
          localStorage.setItem("name", data.user.name);
          localStorage.setItem("role", data.user.role);
          localStorage.setItem("rememberMe", "true");
        } else {
          // Pokud není zaškrtnut, vyčisti localStorage
          localStorage.removeItem("userId");
          localStorage.removeItem("name");
          localStorage.removeItem("role");
          localStorage.removeItem("rememberMe");
        }
        localStorage.setItem("lastUserId", data.user._id); // Uložíme jako posledního uživatele vždy
        setUserId(data.user._id);
        setName(data.user.name);
        setUserRole(data.user.role);
        setCurrentUser(data.user); // Nastavíme celý user objekt
        setOriginalUserId(data.user._id); // Uložíme původní ID
        setIsLoggedIn(true);
        if (data.user.role !== 'superadmin') {
          fetchCompanies(data.user._id);
        }
        fetchTenantLogo(data.user._id);
        // Re-načti users s userId aby se uplatnil tenant filtr
        fetch(`${API_URL}/users?userId=${data.user._id}`)
          .then(res => res.json())
          .then(usersData => {
            const teamleaderForIds = data.user.teamleaderFor || [];
            const filteredUsers = usersData.filter(user =>
              user.role !== 'database' || data.user.role === 'admin' || teamleaderForIds.includes(user._id)
            );
            setUsers(filteredUsers);
          })
          .catch(() => {});
      } else if (res.status === 423) {
        // Účet je zablokován
        alert(data.message || "Účet je dočasně zablokován.");
      } else {
        // Neplatné přihlašovací údaje
        alert(data.message || "Chyba při přihlášení");
      }
    } catch (err) {
      console.error("Login error:", err);
      alert("Chyba při komunikaci se serverem: " + err.message);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode.trim()) {
      alert("Zadejte ověřovací kód");
      return;
    }
    if (isLoginLoading) return;
    try {
      setIsLoginLoading(true);
      const res = await fetch(`${API_URL}/auth/verify-2fa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: twoFAUserId, code: twoFACode, trustDevice: twoFATrustDevice }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || "Chyba ověření");
        return;
      }
      // Ulož device token pokud uživatel zaškrtl "důvěřovat tomuto zařízení"
      if (data.deviceToken) {
        localStorage.setItem('deviceToken', data.deviceToken);
      }
      setShow2FAScreen(false);
      setTwoFACode("");
      setTwoFAUserId(null);

      // Standardní login flow
      const user = data.user;
      if (rememberMe) {
        localStorage.setItem("userId", user._id);
        localStorage.setItem("name", user.name);
        localStorage.setItem("role", user.role);
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("userId");
        localStorage.removeItem("name");
        localStorage.removeItem("role");
        localStorage.removeItem("rememberMe");
      }
      localStorage.setItem("lastUserId", user._id);
      setUserId(user._id);
      setName(user.name);
      setUserRole(user.role);
      setCurrentUser(user);
      setOriginalUserId(user._id);
      setIsLoggedIn(true);
      if (user.role !== 'superadmin') {
        fetchCompanies(user._id);
      }
      fetchTenantLogo(user._id);
      fetch(`${API_URL}/users?userId=${user._id}`)
        .then(r => r.json())
        .then(usersData => {
          const teamleaderForIds = user.teamleaderFor || [];
          const filteredUsers = usersData.filter(u =>
            u.role !== 'database' || user.role === 'admin' || teamleaderForIds.includes(u._id)
          );
          setUsers(filteredUsers);
        })
        .catch(() => {});
    } catch (err) {
      console.error("2FA verify error:", err);
      alert("Chyba při komunikaci se serverem: " + err.message);
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Funkce pro ukládání výchozích filtrů na backend
  const saveDefaultFilters = async () => {
    if (!userId) return;
    
    const filtersToSave = {
      statusFilter,
      forecastFilter,
      operatorFilter,
      bmslMin,
      bmslMax,
      prmFilter,
      leadFilter,
      mainSortOrder,
      showCompleted,
      showNotInterested,
      showToday,
      showOverdue
    };
    
    try {
      const response = await fetch(`${API_URL}/users/${userId}/filters`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filters: filtersToSave })
      });
      
      if (response.ok) {
        alert("Výchozí filtry byly uloženy pro vašeho uživatele ✅");
      } else {
        throw new Error("Chyba při ukládání filtrů");
      }
    } catch (err) {
      console.error("Chyba při ukládání filtrů:", err);
      alert("Chyba při ukládání filtrů na server. Zkuste to prosím znovu.");
    }
  };

  // Uložení šablony filtrů
  const saveFilterTemplate = async () => {
    if (!newTemplateName.trim() || !userId) return;
    const filters = {
      statusFilter, forecastFilter, operatorFilter, bmslMin, bmslMax,
      prmFilter, leadFilter, mainSortOrder, showCompleted, showNotInterested, showToday, showOverdue
    };
    try {
      const res = await fetch(`${API_URL}/users/${userId}/filter-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTemplateName.trim(), filters })
      });
      if (res.ok) {
        const data = await res.json();
        setFilterTemplates(data.filterTemplates || []);
        setNewTemplateName('');
        setShowSaveTemplateInput(false);
      }
    } catch (err) {
      console.error('Chyba při ukládání šablony filtru:', err);
    }
  };

  // Smazání šablony filtrů
  const deleteFilterTemplate = async (tplId) => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/users/${userId}/filter-templates/${tplId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId }
      });
      if (res.ok) {
        const data = await res.json();
        setFilterTemplates(data.filterTemplates || []);
        if (tplId === activeTemplateId) {
          setActiveTemplateId(null);
          localStorage.removeItem('activeFilterTemplateId');
        }
      }
    } catch (err) {
      console.error('Chyba při mazání šablony filtru:', err);
    }
  };

  // Aplikování šablony filtrů
  const applyFilterTemplate = (tpl) => {
    const f = tpl.filters || {};
    setStatusFilter(f.statusFilter || []);
    setForecastFilter(f.forecastFilter || []);
    setOperatorFilter(f.operatorFilter || []);
    setBmslMin('bmslMin' in f ? (f.bmslMin || '') : bmslMin);
    setBmslMax('bmslMax' in f ? (f.bmslMax || '') : bmslMax);
    setPrmFilter('prmFilter' in f ? (f.prmFilter || '') : prmFilter);
    setLeadFilter('leadFilter' in f ? (!!f.leadFilter) : leadFilter);
    if (f.mainSortOrder) setMainSortOrder(f.mainSortOrder);
    if (f.showCompleted !== undefined) setShowCompleted(f.showCompleted);
    if (f.showNotInterested !== undefined) setShowNotInterested(f.showNotInterested);
    if (f.showToday !== undefined) setShowToday(f.showToday);
    if (f.showOverdue !== undefined) setShowOverdue(f.showOverdue);
    setActiveTemplateId(tpl.id);
    localStorage.setItem('activeFilterTemplateId', tpl.id);
  };

  // Manuální aktualizace aktivní šablony aktuálními filtry
  const updateFilterTemplate = async () => {
    if (!activeTemplateId || !userId) return;
    const filters = {
      statusFilter, forecastFilter, operatorFilter, bmslMin, bmslMax,
      prmFilter, leadFilter, mainSortOrder, showCompleted, showNotInterested, showToday, showOverdue
    };
    try {
      const res = await fetch(`${API_URL}/users/${userId}/filter-templates/${activeTemplateId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters })
      });
      if (res.ok) {
        const data = await res.json();
        setFilterTemplates(data.filterTemplates || []);
      }
    } catch (err) {
      console.error('Chyba při aktualizaci šablony:', err);
    }
  };

  // Detekce zda se aktuální filtry liší od uložené šablony
  const activeTemplate = filterTemplates.find(t => t.id === activeTemplateId);
  const templateIsDirty = !!activeTemplate && (() => {
    const f = activeTemplate.filters || {};
    const arrEq = (a, b) => JSON.stringify([...(a||[])].sort()) === JSON.stringify([...(b||[])].sort());
    return !arrEq(statusFilter, f.statusFilter) ||
      !arrEq(forecastFilter, f.forecastFilter) ||
      !arrEq(operatorFilter, f.operatorFilter) ||
      (bmslMin||'') !== (f.bmslMin||'') ||
      (bmslMax||'') !== (f.bmslMax||'') ||
      (prmFilter||'') !== (f.prmFilter||'') ||
      !!leadFilter !== !!f.leadFilter ||
      (mainSortOrder||'date') !== (f.mainSortOrder||'date') ||
      !!showToday !== !!f.showToday ||
      !!showOverdue !== !!f.showOverdue;
  })();

  // Funkce pro načtení výchozích filtrů z backendu
  const loadDefaultFilters = async (userId, availableStatuses = [], availableForecasts = []) => {
    try {
      const response = await fetch(`${API_URL}/users/${userId}/filters`);
      
      if (response.ok) {
        const data = await response.json();
        const filters = data.savedFilters;
        
        if (filters && Object.keys(filters).length > 0) {
          // Filtruj pouze existující stavy a forecast hodnoty
          const validStatusFilter = (filters.statusFilter || []).filter(status => 
            availableStatuses.length === 0 || availableStatuses.includes(status)
          );
          const validForecastFilter = (filters.forecastFilter || []).filter(forecast => 
            availableForecasts.length === 0 || availableForecasts.includes(forecast)
          );
          
          setStatusFilter(validStatusFilter);
          setForecastFilter(validForecastFilter);
          setOperatorFilter(filters.operatorFilter || []);
          setBmslMin(filters.bmslMin || "");
          setBmslMax(filters.bmslMax || "");
          setPrmFilter(filters.prmFilter || "");
          setLeadFilter(filters.leadFilter || false);
          setMainSortOrder(filters.mainSortOrder || 'date');
          setShowCompleted(filters.showCompleted !== undefined ? filters.showCompleted : true);
          setShowNotInterested(filters.showNotInterested !== undefined ? filters.showNotInterested : false);
          setShowToday(filters.showToday !== undefined ? filters.showToday : false);
          setShowOverdue(filters.showOverdue !== undefined ? filters.showOverdue : false);
          console.log("Filtry načteny ze serveru pro uživatele:", userId, filters);
          console.log("Platné filtry aplikovány:", { validStatusFilter, validForecastFilter });
        } else {
          // Pokud uživatel nemá uložené filtry, použij výchozí hodnoty
          console.log("Uživatel nemá uložené filtry, použiji výchozí hodnoty");
          setStatusFilter([]);
          setForecastFilter([]);
          setShowCompleted(true);
          setShowNotInterested(false);
          // showToday a showOverdue są zachovány z localStorage
        }
        // Načti šablony filtrů (vždy, bez ohledu na výchozí filtry)
        setFilterTemplates(data.filterTemplates || []);
        // Obnov aktivní šablonu po refreshi
        const savedActiveId = localStorage.getItem('activeFilterTemplateId');
        if (savedActiveId && data.filterTemplates?.length > 0) {
          const activeTpl = data.filterTemplates.find(t => t.id === savedActiveId);
          if (activeTpl) applyFilterTemplate(activeTpl);
        }
      } else {
        throw new Error("Chyba při načítání filtrů ze serveru");
      }
    } catch (err) {
      console.error("Chyba při načítání filtrů ze serveru:", err);
      // Použij výchozí hodnoty při chybě
      setStatusFilter([]);
      setForecastFilter([]);
      setShowCompleted(true);
      setShowNotInterested(false);
      // showToday a showOverdue jsou zachovány z localStorage při chybě
    }
  };

  const handleSaveNewCompany = async (form) => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          createdBy: userId,
          userId: userId,
          // Používáme form.activities, které už obsahuje aktivity připravené z CompanyForm
          activities: form.activities || [],
        }),
      });
      if (res.ok) {
        const savedCompany = await res.json();
        fetchCompanies(userId);
        // Po uložení přejdeme na detail firmy (stejně jako u editace existující firmy)
        return savedCompany;
      } else {
        const data = await res.json();
        alert(data.message || "Chyba při ukládání firmy");
      }
    } catch (err) {
      alert("Chyba při komunikaci se serverem");
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm("Opravdu chcete tuto společnost smazat?")) return;
    try {
      const res = await fetch(`${API_URL}/companies/${companyId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        fetchCompanies(userId);
      } else {
        alert("Chyba při mazání společnosti");
      }
    } catch (err) {
      alert("Chyba při komunikaci se serverem");
    }
  };

  // Pomocné funkce pro datum
  function isOverdue(date) {
    if (!date) return false;
    const actionDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return actionDate < today;
  }

  function isToday(date) {
    if (!date) return false;
    const actionDate = new Date(date);
    const today = new Date();
    return isSameDay(actionDate, today);
  }

  // Funkce pro zobrazení ikony forecastu
  const getForecastIcon = (forecast) => {
    if (!forecast || forecast === 0) return "";
    
    switch (parseInt(forecast)) {
      case 50:
        return "🔶";
      case 75:
        return "🔸";
      case 100:
        return "💎";
      default:
        return "";
    }
  };

  // Funkce pro tooltip forecastu
  const getForecastTooltip = (forecast) => {
    if (!forecast || forecast === 0) return "";
    
    switch (parseInt(forecast)) {
      case 50:
        return "50% úspěšnost";
      case 75:
        return "75% úspěšnost - téměř jistý";
      case 100:
        return "100% úspěšnost - výhra";
      default:
        return "";
    }
  };

  // Výpočet aktivit podle nextActionDate
  const today = new Date();
  const plannedToday = companies.filter(c => {
    if (!c.nextActionDate) return false;
    const d = new Date(c.nextActionDate);
    if (!isSameDay(d, today)) return false;
    
    // Vyloučení negativních stavů (stejně jako u propadlých)
    const statusInDb = availableStatuses.find(status => status.name === c.status);
    if (statusInDb && statusInDb.excludeFromOverdue) {
      return false;
    }
    
    // Fallback pro stavy, které nejsou v databázi
    if (!statusInDb) {
      const negativeStatuses = [
        'Hotovo', 
        'Nemá zájem', 
        'Odmítnut', 
        'Odmítnuto',
        'Ukončen',
        'Ukončeno',
        'Stornován',
        'Stornováno',
        'Zrušen',
        'Zrušeno'
      ];
      
      if (negativeStatuses.includes(c.status)) return false;
    }
    
    return true;
  }).length;

  // Výpočet propadlých aktivit - použij data z databáze
  const overdueCount = companies.filter(c => {
    if (!c.nextActionDate) return false;
    
    // Najdi stav v databázi stavů a zkontroluj, zda není vyloučený
    const statusInDb = availableStatuses.find(status => status.name === c.status);
    if (statusInDb && statusInDb.excludeFromOverdue) {
      return false; // Vyloučen z propadlých aktivit
    }
    
    // Fallback pro stavy, které nejsou v databázi - použij původní logiku
    if (!statusInDb) {
      const negativeStatuses = [
        'Hotovo', 
        'Nemá zájem', 
        'Odmítnut', 
        'Odmítnuto',
        'Ukončen',
        'Ukončeno',
        'Stornován',
        'Stornováno',
        'Zrušen',
        'Zrušeno'
      ];
      
      if (negativeStatuses.includes(c.status)) return false;
    }
    
    return isOverdue(c.nextActionDate);
  }).length;

  // Stavy pro filtr = přesně to co je v DB (stejný seznam jako v CompanyForm)
  // + případné nestandardní stavy z dat (např. stavy nahraná importem)
  const dbStatusNames = availableStatuses.map(s => s.name || s).filter(Boolean);
  const uniqueStatuses = [
    ...dbStatusNames,
    ...[...new Set(companies
      .map(c => c.status)
      .filter(s => s && s.trim() !== "" && !dbStatusNames.includes(s))
    )]
  ];

  // Unikátní forecast hodnoty
  const uniqueForecasts = [...new Set(companies
    .map(company => company.forecast)
    .filter(forecast => forecast !== undefined && forecast !== null && forecast !== "")
  )].sort((a, b) => parseFloat(a) - parseFloat(b));

  // Unikátní operátoři
  const uniqueOperators = [...new Set(companies
    .map(company => company.operator)
    .filter(op => op && op.trim() !== "")
  )].sort();

  // Funkce pro získání emoji ikony podle stavu
  const getStatusEmoji = (status) => {
    const statusEmojiMap = {
      'Nová': '✨',
      'Volat později': '⏰',
      'Nedovoláno': '📞',
      'Čekám na podklady': '📄',
      'Vytvořit nabídku': '💼',
      'Zákazník se rozhoduje': '🤔',
      'Vytvořit smlouvu': '📝',
      'Implementace': '⚙️',
      'Hotovo': '✅',
      'Nemá zájem': '❌',
      'Kontaktován': '☎️',
      'Zájem': '👍',
      'Schůzka domluvena': '🤝',
      'Návrh zaslán': '📧',
      'V jednání': '💬',
      'Nedaří se spojit': '📵',
      'Odloženo': '⏸️',
      'Nový': '⭐',
      'Nově': '🆕',
      'Nové': '🆕',
      'Nový kontakt': '🆕'
    };
    return statusEmojiMap[status] || '📋';
  };

  // Funkce pro toggle stavu v multi-select filtru
  const toggleStatusFilter = (status) => {
    if (statusFilter.includes(status)) {
      setStatusFilter(statusFilter.filter(s => s !== status));
    } else {
      setStatusFilter([...statusFilter, status]);
    }
    // ZMĚNA: Nerestuji ostatní filtry - nechám uživatele kombinovat filtry
    // Dropdown necháme otevřený pro výběr více stavů
  };

  // Funkce pro toggle forecastu v multi-select filtru
  const toggleForecastFilter = (forecast) => {
    if (forecastFilter.includes(forecast)) {
      setForecastFilter(forecastFilter.filter(f => f !== forecast));
    } else {
      setForecastFilter([...forecastFilter, forecast]);
    }
  };

  // Funkce pro toggle operátora v multi-select filtru
  const toggleOperatorFilter = (operator) => {
    if (operatorFilter.includes(operator)) {
      setOperatorFilter(operatorFilter.filter(o => o !== operator));
    } else {
      setOperatorFilter([...operatorFilter, operator]);
    }
  };

  // Funkce pro normalizaci telefonních čísel pro lepší hledání
  const normalizePhone = (phone) => {
    if (!phone) return '';
    // Odstraní všechny mezery, pomlčky, závorky a prefix +420
    return phone.replace(/[\s\-\(\)\+]/g, '').replace(/^420/, '');
  };

  // Frontend filtrování a vyhledávání
  const filteredCompanies = getFilteredCompanies(companies, search, {
    statusFilter,
    showOverdue,
    showToday,
    dateFilter,
    bmslMin,
    bmslMax,
    forecastFilter,
    operatorFilter,
    prmFilter,
    leadFilter,
    selectedActivityDay
  });

  // Automatické globální vyhledávání když nenajdeme nic lokálně (admin only)
  useEffect(() => {
    // Pouze pro admina
    if (userRole !== 'admin') {
      setIsGlobalSearch(false);
      setGlobalSearchResults([]);
      return;
    }
    
    // Pokud je vyhledávací dotaz (search) a nejsou žádné lokální výsledky
    if (search && search.trim().length >= 3 && filteredCompanies.length === 0) {
      // Debouncing - počkej 300ms než spustíš globální vyhledávání
      const timer = setTimeout(() => {
        console.log('⚠️ Žádné lokální výsledky, spouštím globální vyhledávání...');
        searchGlobally(search);
      }, 300);
      
      return () => clearTimeout(timer);
    } else if (!search || search.trim().length < 3) {
      // Reset globálního vyhledávání když se search vymaže
      setIsGlobalSearch(false);
      setGlobalSearchResults([]);
    } else if (filteredCompanies.length > 0) {
      // Pokud máme lokální výsledky, zruš globální vyhledávání
      setIsGlobalSearch(false);
      setGlobalSearchResults([]);
    }
  }, [search, filteredCompanies.length, userRole]);

  // Použití nové helper funkce pro filtrování

  // Určení, která data použít - globální nebo lokální
  const companiesToDisplay = isGlobalSearch ? globalSearchResults : filteredCompanies;

  // Řazení firem podle zvoleného způsobu (výchozí: datum další akce)
  const sortedCompanies = [...companiesToDisplay].sort((a, b) => {
    if (mainSortOrder === 'bmsl_asc') {
      return (parseFloat(a.bmsl) || 0) - (parseFloat(b.bmsl) || 0);
    }
    if (mainSortOrder === 'bmsl_desc') {
      return (parseFloat(b.bmsl) || 0) - (parseFloat(a.bmsl) || 0);
    }
    // Výchozí: datum další akce
    if (!a.nextActionDate) return 1;
    if (!b.nextActionDate) return -1;
    return new Date(a.nextActionDate) - new Date(b.nextActionDate);
  });

  // Frontend paginace - rozdělíme seřazené firmy na stránky
  const totalPages = Math.ceil(sortedCompanies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pagedCompanies = sortedCompanies.slice(startIndex, startIndex + itemsPerPage);

  // Reset stránky na 1 při změně filtru
  useEffect(() => {
    setCurrentPage(1);
  }, [search, showCompleted, showNotInterested, showOverdue, showToday, dateFilter, activityDateFilter, statusFilter, forecastFilter, selectedActivityDay]);

  // Zavření dropdown při kliknutí mimo něj
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFiltersPanel && !event.target.closest('.filters-panel-container')) {
        setShowFiltersPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFiltersPanel]);

  // Přihlašovací obrazovka — přeskočit pro veřejné leadtool stránky
  if (!isLoggedIn && !location.pathname.startsWith('/leadtool')) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${isDarkMode ? 'bg-gradient-to-br from-gray-900 via-red-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-indigo-100 to-purple-100'}`}>
        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl w-full max-w-md border-0 dark:border dark:border-gray-700">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center mb-6">
              <VyjednameLogo width={300} height={75} variant="vertical" className="mb-2" logoUrl={tenantLogo} tenantName={tenantName} />
            </div>
          </div>

          {show2FAScreen ? (
            <>
              <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white text-center">Dvoufaktorové ověření</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">Ověřovací kód byl odeslán na váš email. Kód je platný 10 minut.</p>
              {twoFAEmailFailed && (
                <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-lg p-3 mb-4 text-sm text-orange-700 dark:text-orange-300 text-center">
                  ⚠️ Email se nepodařilo odeslat. Kód je zaznamenán v serverovém logu (Render Dashboard → Logs). Zkontrolujte log a zadejte kód ručně.
                </div>
              )}
              <div className="space-y-4">
                <input
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors duration-200 text-base text-center tracking-widest text-2xl font-bold"
                  placeholder="000000"
                  maxLength={6}
                  value={twoFACode}
                  onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  onKeyPress={e => e.key === 'Enter' && handleVerify2FA()}
                  autoFocus
                />
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="trustDevice"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    checked={twoFATrustDevice}
                    onChange={e => setTwoFATrustDevice(e.target.checked)}
                  />
                  <label htmlFor="trustDevice" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Příště na tomto zařízení ověření nevyžadovat
                  </label>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-4 rounded-xl font-semibold text-base transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  onClick={handleVerify2FA}
                  disabled={isLoginLoading}
                >
                  {isLoginLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Ověřuji…
                    </span>
                  ) : 'Ověřit kód'}
                </button>
                <button
                  className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 py-2"
                  onClick={() => { setShow2FAScreen(false); setTwoFACode(""); setTwoFAUserId(null); setTwoFAEmailFailed(false); }}
                >
                  Zpět na přihlášení
                </button>
              </div>
            </>
          ) : (
            <>
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white text-center">Přihlášení</h2>
          
          <div className="space-y-4">
            <div>
              <input
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors duration-200 text-base"
                placeholder="Uživatelské jméno"
                value={username}
                onChange={e => setUsername(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleLogin()}
              />
            </div>
            
            <div>
              <input
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors duration-200 text-base"
                type="password"
                placeholder="Heslo"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleLogin()}
              />
            </div>
            
            <div className="flex items-center mt-4 mb-6">
              <input
                type="checkbox"
                id="rememberMe"
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Zapamatovat si mě
              </label>
            </div>
            
            <button
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-4 rounded-xl font-semibold text-base transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
              onClick={handleLogin}
              disabled={isLoginLoading}
            >
              {isLoginLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Přihlašuji…
                </span>
              ) : 'Přihlásit se'}
            </button>

            <div className="relative flex items-center my-1">
              <div className="flex-grow border-t border-gray-200 dark:border-gray-600"></div>
              <span className="mx-3 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">nebo</span>
              <div className="flex-grow border-t border-gray-200 dark:border-gray-600"></div>
            </div>
            <a
              href="/#/leadtool"
              className="w-full flex items-center justify-center gap-2 border-2 border-gray-200 dark:border-gray-600 hover:border-red-400 dark:hover:border-red-500 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200"
            >
              🤝 Přihlášení pro dodavatele (Leadtool)
            </a>
          </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Vyjednáme s.r.o. © 2026
          </p>
        </div>
      </div>
    );
  }

  // Superadmin konzole
  if (isLoggedIn && userRole === 'superadmin') {
    const handleSuperAdminLogout = () => {
      setIsLoggedIn(false);
      setUserRole('');
      setUserId('');
      setCurrentUser(null);
      localStorage.removeItem("userId");
      localStorage.removeItem("name");
      localStorage.removeItem("role");
    };
    return <SuperAdminConsole onLogout={handleSuperAdminLogout} />;
  }

  // Hlavní stránka
  const workingDays = calculateWorkingDays(new Date(), 10);
  const activitiesData = getActivitiesForDays(companies, workingDays);

  // Pomocné funkce pro dynamické grafy
  const getChartConfig = (chartKey) => {
    if (!chartSettings || !chartSettings.charts || !chartSettings.charts[chartKey]) {
      // Výchozí konfigurace pokud není načteno z API
      return chartKey === 'companyCount' 
        ? {
            max: 1000,
            ranges: [
              { min: 0, max: 200, color: '#ef4444', message: '<name>, koukej makat! 😤' },
              { min: 200, max: 500, color: '#f97316', message: 'Dobrý start, <name>! 💪' },
              { min: 500, max: 800, color: '#eab308', message: 'Skvělá práce, <name>! ⭐' },
              { min: 800, max: 1000, color: '#22c55e', message: 'Jsi borec/ka, <name>! 🎉' }
            ]
          }
        : {
            max: 20000,
            ranges: [
              { min: 0, max: 8000, color: '#ef4444', message: '<name>, koukej zabrat! 😬' },
              { min: 8000, max: 12000, color: '#f97316', message: 'Vypadá to dobře, <name>! 👍' },
              { min: 12000, max: 20000, color: '#22c55e', message: 'Skvěle, <name>! Jsi na top! 🚀' }
            ]
          };
    }
    return chartSettings.charts[chartKey];
  };

  const getColorForValue = (value, chartKey) => {
    const config = getChartConfig(chartKey);
    const range = config.ranges.find(r => value >= r.min && value < r.max) || config.ranges[config.ranges.length - 1];
    return range ? range.color : '#3b82f6';
  };

  const getMessageForValue = (value, chartKey) => {
    const config = getChartConfig(chartKey);
    const range = config.ranges.find(r => value >= r.min && value < r.max) || config.ranges[config.ranges.length - 1];
    if (!range) return 'Pokračuj v práci!';
    
    // Pro BMSL graf vybere zprávu podle fáze měsíce
    let message;
    if (chartKey === 'bmslDeals' && range.messages) {
      const dayOfMonth = new Date().getDate();
      if (dayOfMonth <= 10) {
        message = range.messages.early || range.message || 'Dobrý začátek!';
      } else if (dayOfMonth <= 20) {
        message = range.messages.mid || range.message || 'Pokračuj!';
      } else {
        message = range.messages.late || range.message || 'Závěrečná fáze!';
      }
    } else {
      message = range.message;
    }
    
    // Nahraď <name> jménem nebo přezdívkou uživatele
    if (chartSettings && chartSettings.userNicknames && chartSettings.userNicknames[userId]) {
      message = message.replace(/<name>/g, chartSettings.userNicknames[userId]);
    } else if (name) {
      const firstName = name.split(' ')[0];
      message = message.replace(/<name>/g, firstName);
    }
    
    return message;
  };

  const getProgressPosition = (value, chartKey) => {
    const config = getChartConfig(chartKey);
    const clamped = Math.min(Math.max(value, 0), config.max);
    return (clamped / config.max) * 100;
  };

  const generateGradient = (chartKey) => {
    const config = getChartConfig(chartKey);
    const ranges = config.ranges;
    const max = config.max;
    
    // Seřaď rozsahy podle min hodnoty
    const sortedRanges = [...ranges].sort((a, b) => a.min - b.min);
    
    if (sortedRanges.length === 0) return 'linear-gradient(90deg, #ccc 0%, #ccc 100%)';
    
    // Vytvoř plyn ulé přechody mezi barvami s velkou přechodovou zónou
    const gradientStops = [];
    const transitionSize = 20; // 20% přechodová zóna pro velmi hladký přechod
    
    sortedRanges.forEach((range, index) => {
      const startPercent = (range.min / max) * 100;
      const endPercent = (range.max / max) * 100;
      
      if (index === 0) {
        // První rozsah - začíná od své barvy
        gradientStops.push(`${range.color} ${startPercent}%`);
        
        if (sortedRanges.length === 1) {
          // Jediný rozsah
          gradientStops.push(`${range.color} ${endPercent}%`);
        } else {
          // Přechod na další barvu
          const nextRange = sortedRanges[index + 1];
          const transitionStart = Math.max(endPercent - transitionSize, startPercent);
          gradientStops.push(`${range.color} ${transitionStart}%`);
          gradientStops.push(`${nextRange.color} ${endPercent}%`);
        }
      } else if (index === sortedRanges.length - 1) {
        // Poslední rozsah - už má začátek z předchozího
        gradientStops.push(`${range.color} ${endPercent}%`);
      } else {
        // Prostřední rozsahy
        const nextRange = sortedRanges[index + 1];
        const transitionStart = Math.max(endPercent - transitionSize, startPercent);
        gradientStops.push(`${range.color} ${transitionStart}%`);
        gradientStops.push(`${nextRange.color} ${endPercent}%`);
      }
    });
    
    return `linear-gradient(90deg, ${gradientStops.join(', ')})`;
  };

  // Vytvoření widgetu přímo v App.js
  const CompanyWidget = ({ companies }) => {
    const companyCount = companies.length;
    const config = getChartConfig('companyCount');
    
    // Kontrola zda je graf povolen pro uživatele
    if (chartSettings?.userSettings?.[userId]?.companyCount === false) {
      return null;
    }
    
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow w-64 h-[150px] flex flex-col justify-between flex-shrink-0">
        {/* 1. řádek - název grafu */}
        <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 text-center leading-tight">
          Počet firem v CRM
        </h3>
        
        {/* 2. řádek - hodnota */}
        <div className="text-center">
          <span className="text-3xl font-bold" style={{ color: getColorForValue(companyCount, 'companyCount') }}>
            {companyCount}
          </span>
        </div>
        
        {/* 3. řádek - poznámka */}
        <div className="text-center">
          <span className="text-sm text-gray-600 dark:text-gray-400 leading-tight">
            {getMessageForValue(companyCount, 'companyCount')}
          </span>
        </div>
        
        {/* 4. řádek - progress bar */}
        <div className="w-full px-2">
          <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mb-1">
            <span>0</span>
            <span>{config.max}+</span>
          </div>
          <div className="relative">
            <div 
              className="h-2 rounded-full"
              style={{ 
                background: generateGradient('companyCount'),
                opacity: 0.8
              }}
            ></div>
            <div 
              className="absolute top-0 h-2 w-1 bg-gray-800 dark:bg-gray-200 rounded-full transform -translate-x-1/2"
              style={{ 
                left: `${getProgressPosition(companyCount, 'companyCount')}%`,
                boxShadow: '0 0 4px rgba(0,0,0,0.5)'
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem("darkMode", newDarkMode.toString());
  };

  // Komponenta pro graf aktivit
  const ActivityChart = ({ activitiesData, workingDays }) => {
    const getDayName = (date) => {
      const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
      return days[date.getDay()];
    };

    const handleDayClick = (day) => {
      const dayStr = day.toISOString().split('T')[0];
      
      // Pokud kliknu na stejný den, zruším výběr
      if (selectedActivityDay === dayStr) {
        setSelectedActivityDay(null);
        localStorage.removeItem('homepage_selected_activity_day');
      } else {
        setSelectedActivityDay(dayStr);
        localStorage.setItem('homepage_selected_activity_day', dayStr);
      }
    };

    // Převeď pracovní dny na jen 9 dnů pro jednotnost s kalendářem
    const displayDays = workingDays.slice(0, 9);
    const displayData = activitiesData.slice(0, 9);
    
    const maxActivities = Math.max(...displayData, 1);
    
    // Výška pro hlavní stránku - fixní 135px (z původního kontejneru)
    const maxBarHeight = Math.max((maxActivities / maxActivities) * 80, 8); // max výška sloupce
    const textSpace = 35; // prostor pro text dne a data
    const containerHeight = 135; // fixní výška pro hlavní stránku

    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow w-80 h-[150px] flex-shrink-0">
        <div className="h-[122px]">
          <div className="flex items-end gap-0.5 h-full">
            {displayDays.map((day, index) => {
              const activityCount = displayData[index];
              const height = Math.max((activityCount / maxActivities) * 80, 8); // přizpůsobeno na 80px max
              const dayStr = day.toISOString().split('T')[0];
              const isSelected = selectedActivityDay === dayStr;

              // Barevné schéma podle počtu aktivit
              let colorClass = '';
              if (activityCount <= 40) {
                colorClass = isSelected ? 'bg-green-700 hover:bg-green-800' : 'bg-green-500 hover:bg-green-600';
              } else if (activityCount <= 60) {
                colorClass = isSelected ? 'bg-yellow-700 hover:bg-yellow-800' : 'bg-yellow-500 hover:bg-yellow-600';
              } else if (activityCount <= 80) {
                colorClass = isSelected ? 'bg-orange-700 hover:bg-orange-800' : 'bg-orange-500 hover:bg-orange-600';
              } else {
                colorClass = isSelected ? 'bg-red-700 hover:bg-red-800' : 'bg-red-500 hover:bg-red-600';
              }

              // Zjisti, zda je aktuální den pondělí a předchozí byl pátek
              const isMonday = day.getDay() === 1;
              const shouldShowSeparator = index > 0 && isMonday;

              return (
                <React.Fragment key={index}>
                  {/* Vertikální oddělovač mezi týdny */}
                  {shouldShowSeparator && (
                    <div className="flex flex-col items-center justify-end px-0.5 h-full">
                      <div className="w-px bg-gray-300 dark:bg-gray-500 opacity-30" style={{ height: '129px' }}></div>
                    </div>
                  )}
                  
                  <div className="flex flex-col items-center justify-end flex-1 h-full">
                    <div className="text-[10px] mb-0.5 text-gray-600 dark:text-gray-400 font-medium">
                      {getDayName(day)}
                    </div>
                    <div className="text-[9px] mb-1 text-gray-500 dark:text-gray-500">
                      {day.getDate()}.{day.getMonth() + 1}
                    </div>
                    <div className="flex-1 flex items-end w-full">
                      <div 
                        className={`relative transition-all duration-200 rounded-t-sm w-full cursor-pointer ${colorClass} ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                        style={{ 
                          height: `${height}px`, 
                          minHeight: '12px'
                        }}
                        onClick={() => handleDayClick(day)}
                        title={`${getDayName(day)} ${day.getDate()}.${day.getMonth() + 1}.${day.getFullYear()}: ${activityCount} aktivit${activityCount === 1 ? 'a' : activityCount < 5 ? 'y' : ''}`}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">
                            {activityCount}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Komponenta pro uzavřené smlouvy BMSL
  const ClosedDealsBMSLWidget = ({ companies, upsells = [] }) => {
    // Filtruj firmy s forecast 100 a zobchodované tento měsíc
    const closedDealsThisMonth = companies.filter(c => {
      const forecast = parseInt(c.forecast);
      if (forecast !== 100) return false;
      
      // Kontrola zda je z tohoto měsíce
      const checkDate = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      };
      
      return checkDate(c.closedDate) || checkDate(c.zobchodovano);
    });
    
    // Filtruj upselly s forecast 100 a zobchodované tento měsíc
    const closedUpsellsThisMonth = upsells.filter(u => {
      const forecast = parseInt(u.forecast);
      if (forecast !== 100) return false;
      
      // Kontrola zda je z tohoto měsíce
      const checkDate = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      };
      
      return checkDate(u.closedDate);
    });
    
    // Vypočti celkové BMSL z firem i upsellů
    const totalBMSL = 
      closedDealsThisMonth.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0) +
      closedUpsellsThisMonth.reduce((sum, u) => sum + (parseFloat(u.bmsl) || 0), 0);
    
    const config = getChartConfig('bmslDeals');
    
    // Kontrola zda je graf povolen pro uživatele
    if (chartSettings?.userSettings?.[userId]?.bmslDeals === false) {
      return null;
    }
    
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow w-64 h-[150px] flex flex-col justify-between flex-shrink-0">
        {/* 1. řádek - název grafu */}
        <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 text-center leading-tight">
          Uzavřené smlouvy BMSL
        </h3>
        
        {/* 2. řádek - hodnota */}
        <div className="text-center">
          <span className="text-3xl font-bold" style={{ color: getColorForValue(totalBMSL, 'bmslDeals') }}>
            {Math.round(totalBMSL).toLocaleString('cs-CZ')} Kč
          </span>
        </div>
        
        {/* 3. řádek - poznámka */}
        <div className="text-center">
          <span className="text-sm text-gray-600 dark:text-gray-400 leading-tight">
            {getMessageForValue(totalBMSL, 'bmslDeals')}
          </span>
        </div>
        
        {/* 4. řádek - progress bar */}
        <div className="w-full px-2">
          <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mb-1">
            <span>0 Kč</span>
            <span>{config.max.toLocaleString('cs-CZ')} Kč</span>
          </div>
          <div className="relative">
            <div 
              className="h-2 rounded-full"
              style={{ 
                background: generateGradient('bmslDeals'),
                opacity: 0.8
              }}
            ></div>
            <div 
              className="absolute top-0 h-2 w-1 bg-gray-800 dark:bg-gray-200 rounded-full transform -translate-x-1/2"
              style={{ 
                left: `${getProgressPosition(totalBMSL, 'bmslDeals')}%`,
                boxShadow: '0 0 4px rgba(0,0,0,0.5)'
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Funkce pro generování konfigurace tlačítek
  const generateButtonsConfig = (isMenuOpen, setIsMenuOpen, isFiltersOpen, setIsFiltersOpen) => {
    const buttons = [
      {
        id: 'account',
        label: 'Správa',
        icon: '⚙️',
        colorClass: 'btn-gray',
        onClick: () => { navigate('/sprava'); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: '📊',
        colorClass: 'btn-indigo',
        onClick: () => { navigate('/dashboard'); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'logout',
        label: 'Odhlásit',
        icon: '🔓',
        colorClass: 'btn-red',
        onClick: () => {
          setIsLoggedIn(false);
          localStorage.removeItem("userId");
          localStorage.removeItem("name");
          localStorage.removeItem("role");
          // Nerestuji filtry při odhlášení - uživatel si je chce zachovat
          setCompanies([]);
          setIsMenuOpen && setIsMenuOpen(false);
        }
      },
      {
        id: 'contacts',
        label: 'Cloud',
        icon: '☁️',
        colorClass: 'btn-cyan',
        onClick: () => { navigate('/cloud'); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'activities',
        label: 'Aktivity',
        icon: '📋',
        colorClass: 'btn-teal',
        onClick: () => { navigate('/activities'); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'darkmode',
        label: 'Tmavý režim',
        icon: '🌙',
        colorClass: 'btn-black',
        onClick: () => { toggleDarkMode(); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'commissions',
        label: 'Provize',
        icon: '💰',
        colorClass: 'btn-yellow',
        onClick: () => { navigate('/commissions'); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'requests',
        label: 'Požadavky',
        icon: '📝',
        colorClass: 'btn-purple',
        onClick: () => { navigate('/requests'); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'meetingplanner',
        label: 'Schůzky',
        icon: '🗺️',
        colorClass: 'btn-pink',
        onClick: () => { setShowMeetingPlanner(true); setIsMenuOpen && setIsMenuOpen(false); }
      },
      {
        id: 'backup',
        label: 'Záloha',
        icon: '💾',
        colorClass: 'btn-gray',
        onClick: () => { navigate('/backup'); setIsMenuOpen && setIsMenuOpen(false); },
        hidden: !hasPermission('viewAdmin')
      },
      {
        id: 'restore-backup',
        label: 'Obnova ze zálohy',
        icon: '♻️',
        colorClass: 'btn-gray',
        onClick: () => { navigate('/restore-backup'); setIsMenuOpen && setIsMenuOpen(false); },
        hidden: !hasPermission('viewAdmin')
      },
      {
        id: 'phonesync',
        label: 'Synchronizace s PC',
        icon: '📱',
        colorClass: 'btn-green',
        onClick: () => { navigate('/phone-sync'); setIsMenuOpen && setIsMenuOpen(false); },
        // Zobrazit pouze na mobilních zařízeních
        hidden: window.innerWidth > 768
      }
    ];

    return buttons;
  };

  const DashboardLayout = () => {
    const workingDays = calculateWorkingDays(new Date(), 10);
    const activitiesData = getActivitiesForDays(companies, workingDays);

    return (
      <div className="flex flex-col lg:flex-row gap-3 justify-between items-stretch min-w-0">
        {/* Levý sloupec - Název společnosti a uvítání + mobilní navigace na celou šířku */}
        <div className="flex flex-col w-full lg:w-auto flex-shrink-0">
          {/* Desktop verze – velké obrazovky (lg+): logo + uvítání */}
          <div className="hidden lg:block mb-3 py-1 mt-4">
            <VyjednameLogo width={300} height={80} className="mb-4" logoUrl={tenantLogo} tenantName={tenantName} />
            <p className="text-xl text-gray-600 dark:text-gray-500 ml-0">
              Vítejte, <span className="font-semibold text-blue-600 dark:text-blue-400">{name}</span>
            </p>
          </div>

          {/* Desktop verze – menší obrazovky (lg pouze): pouze prázdný placeholder */}
          <div className="hidden lg:flex xl:hidden items-center gap-4 mb-3 py-1 mt-4">
          </div>
          
          {/* Mobilní verze - logo a vítejte pod sebou s Menu tlačítkem */}
          <div className="lg:hidden mb-3 py-1 mt-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-0.5">
                <VyjednameLogo width={180} height={45} logoUrl={tenantLogo} tenantName={tenantName} className="mb-1" />
                <p className="text-sm text-gray-600 dark:text-gray-400 ml-0">
                  Vítejte, <span className="font-semibold text-blue-600 dark:text-blue-400">{name}</span>
                </p>
              </div>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-semibold transition flex items-center gap-2 text-sm min-h-[60px] flex-shrink-0"
              >
                <div className="flex flex-col gap-0.5">
                  <div className="w-4 h-0.5 bg-white"></div>
                  <div className="w-4 h-0.5 bg-white"></div>
                  <div className="w-4 h-0.5 bg-white"></div>
                </div>
                Menu
              </button>
            </div>
          </div>
          
          {/* Mobilní navigace - pouze hamburger menu obsah */}
          <div className="lg:hidden xl:hidden mb-4 w-full max-w-full">
            {/* Hamburger menu obsah - scrollovatelné s tlačítky ve 2 sloupcích */}
            {isMenuOpen && (
              <div className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow w-full max-w-full max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {/* Správa - první řádek vlevo */}
                  <button
                    onClick={() => {
                      navigate('/sprava');
                      setIsMenuOpen(false);
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    ⚙️ Správa
                  </button>
                  
                  {/* Odhlásit - první řádek vpravo */}
                  <button
                    onClick={() => {
                      setIsLoggedIn(false);
                      localStorage.removeItem("userId");
                      localStorage.removeItem("name");
                      localStorage.removeItem("role");
                      setCompanies([]);
                      setIsMenuOpen(false);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    🚪 Odhlásit
                  </button>
                  
                  {/* Přidat novou firmu */}
                  <button
                    onClick={() => {
                      navigate('/companies/new');
                      setIsMenuOpen(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    ➕ Přidat firmu
                  </button>
                  
                  {/* Nové kontakty - pomocí NewContactsButton pro stejnou funkcionalitu jako desktop */}
                  <NewContactsButton 
                    currentUserId={userId}
                    currentUserName={currentUser?.name}
                    isDarkMode={isDarkMode}
                    onContactsAssigned={() => {
                      userId && fetchCompanies(userId);
                      setIsMenuOpen(false);
                    }}
                    customClassName="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                    customText="📞 Nové kontakty"
                  />
                  {/* Leady tlačítko (mobilní) */}
                  <button
                    onClick={() => { setShowLeadyModal(true); setIsMenuOpen(false); }}
                    className="w-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    🎯 Leady
                  </button>
                  
                  {/* Teamleader tlačítko */}
                  {currentUser && currentUser.teamleaderFor && currentUser.teamleaderFor.length > 0 && (
                    <button
                      onClick={() => {
                        setShowTeamleaderDropdown(!showTeamleaderDropdown);
                      }}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                    >
                      👥 Teamleader
                    </button>
                  )}
                  
                  {/* Teamleader dropdown uvnitř menu */}
                  {showTeamleaderDropdown && currentUser && currentUser.teamleaderFor && currentUser.teamleaderFor.length > 0 && (
                    <div className="col-span-2 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg space-y-2">
                      {/* Tlačítko pro návrat na vlastní data */}
                      {originalUserId && userId !== originalUserId && (
                        <button
                          className="w-full text-left px-4 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg font-semibold"
                          onClick={() => {
                            setUserId(originalUserId);
                            setName(currentUser.name);
                            fetchCompanies(originalUserId);
                            setShowTeamleaderDropdown(false);
                            setIsMenuOpen(false);
                          }}
                        >
                          ↩️ Zpět na moje data
                        </button>
                      )}
                      
                      {/* Seznam podřízených */}
                      {users
                        .filter(u => currentUser.teamleaderFor.includes(u._id))
                        .map(subordinate => (
                          <button
                            key={subordinate._id}
                            className={`w-full text-left px-4 py-2 rounded-lg ${
                              userId === subordinate._id
                                ? 'bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-white font-semibold'
                                : 'bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500'
                            }`}
                            onClick={() => {
                              setUserId(subordinate._id);
                              setName(subordinate.name);
                              fetchCompanies(subordinate._id);
                              setShowTeamleaderDropdown(false);
                              setIsMenuOpen(false);
                            }}
                          >
                            {subordinate.name}
                            {userId === subordinate._id && ' ✓'}
                          </button>
                        ))}
                    </div>
                  )}
                  
                  {/* Filtry tlačítko */}
                  <button
                    onClick={() => {
                      setIsFiltersOpen(!isFiltersOpen);
                      setIsMenuOpen(false);
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    🔽 Filtry
                  </button>
                  
                  {/* Dashboard */}
                  <button
                    onClick={() => {
                      navigate('/dashboard');
                      setIsMenuOpen(false);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    📊 Dashboard
                  </button>
                  
                  {/* Provize */}
                  <button
                    onClick={() => {
                      navigate('/commissions');
                      setIsMenuOpen(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    💰 Provize
                  </button>
                  
                  {/* Požadavky (místo Žádanky) */}
                  <button
                    onClick={() => {
                      navigate('/requests');
                      setIsMenuOpen(false);
                    }}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    📋 Požadavky
                  </button>
                  
                  {/* Aktivity */}
                  <button
                    onClick={() => {
                      navigate('/activities');
                      setIsMenuOpen(false);
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    📅 Aktivity
                  </button>
                  
                  {/* Schůzky */}
                  <button
                    onClick={() => {
                      setShowMeetingPlanner(true);
                      setIsMenuOpen(false);
                    }}
                    className="bg-violet-700 hover:bg-violet-800 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    📆 Schůzky
                  </button>
                  
                  {/* Tmavý režim */}
                  <button
                    onClick={() => {
                      setIsDarkMode(!isDarkMode);
                      setIsMenuOpen(false);
                    }}
                    className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    {isDarkMode ? '☀️ Světlý' : '🌙 Tmavý'}
                  </button>
                  
                  {/* Cloud */}
                  <button
                    onClick={() => {
                      navigate('/cloud');
                      setIsMenuOpen(false);
                    }}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                  >
                    ☁️ Cloud
                  </button>
                  
                  {/* Admin tlačítko - úplně dole */}
                  {hasPermission('viewAdmin') && (
                    <button
                      onClick={() => {
                        navigate('/users');
                        setIsMenuOpen(false);
                      }}
                      className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm min-h-[48px]"
                    >
                      ⚙️ Admin
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Filtry roleta */}
            {isFiltersOpen && (
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow w-full max-w-full">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 w-full max-w-full">
                  {/* Záhlaví s tlačítkem zavřít */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtry</h3>
                    <button
                      onClick={() => setIsFiltersOpen(false)}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl w-8 h-8 flex items-center justify-center"
                      title="Zavřít"
                    >
                      ❌
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 w-full">
                    <button
                      onClick={() => {
                        const v = !showToday; setShowToday(v); localStorage.setItem('filterShowToday', v); // Toggle místo vždy true
                        // Nerestuji ostatní filtry - umožním kombinaci
                        setIsFiltersOpen(false);
                      }}
                      className={`px-4 py-3 rounded-lg text-base font-semibold transition flex items-center justify-center gap-2 min-h-[50px] w-full ${
                        showToday 
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      📅 Dnes ({plannedToday})
                    </button>
                    <button
                      onClick={() => {
                        const v = !showOverdue; setShowOverdue(v); localStorage.setItem('filterShowOverdue', v); // Toggle místo vždy true
                        // Nerestuji ostatní filtry - umožním kombinaci
                        setIsFiltersOpen(false);
                      }}
                      className={`px-4 py-3 rounded-lg text-base font-semibold transition flex items-center justify-center gap-2 min-h-[50px] w-full ${
                        showOverdue 
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-orange-600 hover:bg-orange-700 text-white'
                      }`}
                    >
                      🔥 Propadlé ({overdueCount})
                    </button>
                  </div>
                  
                  {/* Multi-select pro filtrování podle stavu - mobilní */}
                  <div className="mt-3 w-full">
                    <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stav</span>
                        {statusFilter.length > 0 && (
                          <button onClick={() => setStatusFilter([])} className="text-xs text-red-600 hover:text-red-800 dark:text-red-400">
                            Vymazat ({statusFilter.length})
                          </button>
                        )}
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {uniqueStatuses.map(status => (
                          <label key={status} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                            <input type="checkbox" checked={statusFilter.includes(status)} onChange={() => toggleStatusFilter(status)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                            <span className="text-sm text-gray-900 dark:text-white">{getStatusEmoji(status)} {status}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Multi-select Forecast - mobilní */}
                  <div className="mt-3 w-full">
                    <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Forecast</span>
                        {forecastFilter.length > 0 && (
                          <button onClick={() => setForecastFilter([])} className="text-xs text-red-600 hover:text-red-800 dark:text-red-400">
                            Vymazat ({forecastFilter.length})
                          </button>
                        )}
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {uniqueForecasts.map(forecast => (
                          <label key={forecast} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                            <input type="checkbox" checked={forecastFilter.includes(forecast)} onChange={() => toggleForecastFilter(forecast)} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                            <span className="text-sm text-gray-900 dark:text-white">📊 {forecast}%</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Multi-select Operátor - mobilní */}
                  <div className="mt-3 w-full">
                    <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Operátor</span>
                        {operatorFilter.length > 0 && (
                          <button onClick={() => setOperatorFilter([])} className="text-xs text-red-600 hover:text-red-800 dark:text-red-400">
                            Vymazat ({operatorFilter.length})
                          </button>
                        )}
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {uniqueOperators.map(op => (
                          <label key={op} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                            <input type="checkbox" checked={operatorFilter.includes(op)} onChange={() => toggleOperatorFilter(op)} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                            <span className="text-sm text-gray-900 dark:text-white">{op}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* BMSL filtr - mobilní */}
                  <div className="mt-3 w-full">
                    <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                      <div className="mb-2"><span className="text-sm font-medium text-gray-700 dark:text-gray-300">BMSL</span></div>
                      <input type="number" placeholder="Vyšší než..." value={bmslMin} onChange={e => setBmslMin(e.target.value)}
                        className={`w-full px-2 py-1 mb-2 text-sm border rounded ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`} />
                      <input type="number" placeholder="Nižší než..." value={bmslMax} onChange={e => setBmslMax(e.target.value)}
                        className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`} />
                    </div>
                  </div>

                  {/* PRM filtr - mobilní */}
                  <div className="mt-3 w-full">
                    <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                      <div className="mb-2"><span className="text-sm font-medium text-gray-700 dark:text-gray-300">PRM</span></div>
                      <input type="text" placeholder="Filtr PRM..." value={prmFilter} onChange={e => setPrmFilter(e.target.value)}
                        className={`w-full px-2 py-1 text-sm border rounded ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`} />
                    </div>
                  </div>

                  {/* Lead filtr - mobilní */}
                  <div className="mt-3 w-full">
                    <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={leadFilter} onChange={() => setLeadFilter(!leadFilter)}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">🏷 Pouze leady</span>
                      </label>
                    </div>
                  </div>

                  {/* Řadit - mobilní */}
                  <div className="mt-3 w-full">
                    <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                      <div className="mb-2"><span className="text-sm font-medium text-gray-700 dark:text-gray-300">Řazení</span></div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setMainSortOrder('date')} className={`text-xs px-2 py-1 rounded ${mainSortOrder === 'date' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200'}`}>📅 Datum akce</button>
                        <button onClick={() => setMainSortOrder('bmsl_asc')} className={`text-xs px-2 py-1 rounded ${mainSortOrder === 'bmsl_asc' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200'}`}>BMSL ↑</button>
                        <button onClick={() => setMainSortOrder('bmsl_desc')} className={`text-xs px-2 py-1 rounded ${mainSortOrder === 'bmsl_desc' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200'}`}>BMSL ↓</button>
                      </div>
                    </div>
                  </div>

                  {/* Šablony filtrů - mobilní */}
                  {filterTemplates.length > 0 && (
                    <div className="mt-3 w-full">
                      <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                        <div className="mb-2 flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Šablony filtrů</span>
                          {activeTemplate && (
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${templateIsDirty ? 'bg-orange-100 border-orange-300 text-orange-700 dark:bg-orange-900/40 dark:border-orange-600 dark:text-orange-300' : 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-600 dark:text-blue-300'}`}>
                              {templateIsDirty ? `✏️ ${activeTemplate.name} (upraveno)` : `📌 aktivní: ${activeTemplate.name}`}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          {filterTemplates.map(tpl => (
                            <div key={tpl.id} className="flex items-center gap-1">
                              <button
                                onClick={() => applyFilterTemplate(tpl)}
                                className={`flex-1 text-left text-sm px-3 py-1.5 rounded-l border transition ${
                                  activeTemplateId === tpl.id
                                    ? 'bg-blue-600 border-blue-700 text-white'
                                    : isDarkMode ? 'bg-blue-900/30 border-blue-700 text-blue-300 hover:bg-blue-800/50' : 'bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100'
                                }`}
                              >
                                {tpl.name}
                              </button>
                              <button
                                onClick={() => deleteFilterTemplate(tpl.id)}
                                className={`text-sm px-2 py-1.5 rounded-r border border-l-0 ${isDarkMode ? 'bg-blue-900/30 border-blue-700 text-red-400 hover:bg-red-900/30' : 'bg-blue-50 border-blue-300 text-red-500 hover:bg-red-50'} transition`}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Uložení / aktualizace šablony - mobilní */}
                  <div className="mt-3 w-full flex flex-col gap-2">
                    {activeTemplateId && templateIsDirty && (
                      <button
                        onClick={updateFilterTemplate}
                        className="w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2"
                        title={`Aktualizovat filtry uložené v šabloně „${activeTemplate?.name}"`}
                      >
                        🔄 Aktualizovat šablonu
                      </button>
                    )}
                    {showSaveTemplateInput ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          autoFocus
                          placeholder="Název šablony..."
                          value={newTemplateName}
                          onChange={e => setNewTemplateName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveFilterTemplate(); if (e.key === 'Escape') { setShowSaveTemplateInput(false); setNewTemplateName(''); } }}
                          className={`flex-1 px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                        />
                        <button onClick={saveFilterTemplate} disabled={!newTemplateName.trim()} className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded-lg font-semibold transition">Uložit</button>
                        <button onClick={() => { setShowSaveTemplateInput(false); setNewTemplateName(''); }} className="px-3 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg transition">✕</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSaveTemplateInput(true)}
                        className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2"
                      >
                        💾 Uložit jako šablonu
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Střední sloupec - Widgety - skryté na malých obrazovkách, nikdy se neposouvají na další řádek */}
        <div className="hidden xl:flex gap-2 justify-center h-[150px] min-w-0 flex-shrink-0">
          {/* Graf aktivit – ve středním sloupci na xl+ i 2xl+ */}
          <div className="hidden xl:block flex-shrink-0">
            <ActivityChart activitiesData={activitiesData} workingDays={workingDays} />
          </div>
          <div className="flex-shrink-0">
            <CompanyWidget companies={companies} />
          </div>
          <div className="flex-shrink-0">
            <ClosedDealsBMSLWidget companies={companies} upsells={upsells} />
          </div>
        </div>

        {/* Navigační tlačítka - desktop vs mobile layout */}
        <div className="hidden lg:flex gap-2 h-[150px] flex-shrink-0">
          <DynamicGrid 
            buttons={generateButtonsConfig()}
            layout="desktop"
            maxButtons={9}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>
    );
  };

  return (
    <VerificationProvider currentUserId={userId}>
    <div className={isDarkMode ? "dark" : ""}> {/* Přidání třídy dark pro tmavý režim */}
      {/* Pevná lišta pro iOS notch/Dynamic Island – nulový layout dopad, jen na iOS PWA */}
      {isIOSPWA && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 'env(safe-area-inset-top)',
          background: isDarkMode ? '#111827' : '#f3f4f6',
          zIndex: 99999,
          pointerEvents: 'none'
        }} />
      )}

      {/* Loading screen pro načítání firem */}
      {isLoadingCompanies && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center shadow-2xl">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Načítám společnosti...</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Zpracovávám databázi firem</p>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={
          <div className={`h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} flex flex-col`} style={{
            height: '100vh',
            height: '100dvh',
            background: isDarkMode ? '#111827' : '#f3f4f6',
            paddingTop: isIOSPWA ? 'env(safe-area-inset-top)' : undefined
          }}>
            {/* Fixní horní část - desktop i mobile */}
            <div className={`w-[98vw] max-w-[1800px] mx-auto py-4 px-2 flex-shrink-0`}>
              
              {DashboardLayout()} {/* Použití nového layoutu pro widgety a tlačítka */}

              {/* Filtrování - pouze pro desktop */}
              <div className="hidden lg:block space-y-3 mt-4">
                <div className="flex items-center gap-2 flex-wrap overflow-visible">
                  {/* Admin tlačítko */}
                  {hasPermission('viewAdmin') && (
                    <button
                      className="bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-800 text-white px-4 py-2 rounded font-semibold transition"
                      onClick={() => navigate('/users')}
                    >
                      ⚙️ Admin
                    </button>
                  )}
                  
                  {/* Teamleader switcher - zobrazí se pouze pokud má uživatel přiřazené podřízené */}
                  {currentUser && currentUser.teamleaderFor && currentUser.teamleaderFor.length > 0 && (
                    <div className="relative">
                      <button
                        className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800 text-white px-4 py-2 rounded font-semibold transition flex items-center gap-2"
                        onClick={() => setShowTeamleaderDropdown(!showTeamleaderDropdown)}
                      >
                        👥 Teamleader
                      </button>
                      
                      {showTeamleaderDropdown && (
                        <div className={`absolute top-full left-0 mt-2 rounded shadow-lg z-[100] min-w-[200px] ${
                          isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                          {/* Tlačítko pro návrat na vlastní data */}
                          {originalUserId && userId !== originalUserId && (
                            <>
                              <button
                                className={`w-full text-left px-4 py-2 font-semibold ${
                                  isDarkMode 
                                    ? 'hover:bg-gray-700 text-blue-400' 
                                    : 'hover:bg-blue-50 text-blue-600'
                                }`}
                                onClick={() => {
                                  setUserId(originalUserId);
                                  setName(currentUser.name);
                                  fetchCompanies(originalUserId);
                                  setShowTeamleaderDropdown(false);
                                }}
                              >
                                ↩️ Zpět na moje data
                              </button>
                              <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>
                            </>
                          )}
                          
                          {/* Seznam podřízených obchodníků */}
                          {users
                            .filter(u => currentUser.teamleaderFor.includes(u._id))
                            .map(subordinate => (
                              <button
                                key={subordinate._id}
                                className={`w-full text-left px-4 py-2 ${
                                  userId === subordinate._id
                                    ? isDarkMode ? 'bg-gray-700 text-white' : 'bg-blue-100 text-blue-800'
                                    : isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-50'
                                }`}
                                onClick={() => {
                                  setUserId(subordinate._id);
                                  setName(subordinate.name);
                                  fetchCompanies(subordinate._id); // Lazy loading - data se načtou až tady
                                  setShowTeamleaderDropdown(false);
                                }}
                              >
                                {subordinate.name}
                                {userId === subordinate._id && ' ✓'}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {userRole === 'callcentrum' && (
                    <button
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-semibold transition flex items-center gap-2"
                      onClick={() => setShowCallCenterStats(true)}
                    >
                      📊 Statistika leadů
                    </button>
                  )}

                  <button
                    className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white px-4 py-2 rounded font-semibold transition flex items-center gap-2"
                    onClick={() => navigate('/companies/new')}
                  >
                    ➕ Přidat firmu
                  </button>
                  <NewContactsButton 
                    currentUserId={userId}
                    currentUserName={currentUser?.name}
                    isDarkMode={isDarkMode}
                    onContactsAssigned={() => userId && fetchCompanies(userId)}
                  />
                  {/* Leady tlačítko (desktop) */}
                  {showLeadyModal && (
                    <LeadyModal
                      currentUserId={userId}
                      isDarkMode={isDarkMode}
                      onClose={() => setShowLeadyModal(false)}
                      onVisibilityChanged={() => {}}
                    />
                  )}
                  <button
                    onClick={() => setShowLeadyModal(true)}
                    className={`px-4 py-2 rounded font-semibold transition flex items-center gap-2 ${
                      isDarkMode ? 'bg-red-700 hover:bg-red-800 text-white' : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                  >
                    🎯 Leady
                  </button>

                  {/* Aktualizace PRM tlačítko (desktop) */}
                  {showPrmUpdateModal && (
                    <PrmUpdateModal
                      companies={companies}
                      userId={userId}
                      isDarkMode={isDarkMode}
                      onClose={() => setShowPrmUpdateModal(false)}
                      onUpdated={() => userId && fetchCompanies(userId)}
                    />
                  )}
                  <button
                    onClick={() => setShowPrmUpdateModal(true)}
                    className={`px-4 py-2 rounded font-semibold transition flex items-center gap-2 ${
                      isDarkMode ? 'bg-indigo-700 hover:bg-indigo-800 text-white' : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                    }`}
                  >
                    🔄 PRM
                  </button>

                  {/* Přeblokace tlačítko (desktop) – modal s PowerApps iframe */}
                  <button
                    onClick={() => setShowPreblokaceModal(true)}
                    className={`px-4 py-2 rounded font-semibold transition flex items-center gap-2 ${
                      isDarkMode ? 'bg-cyan-700 hover:bg-cyan-800 text-white' : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                    }`}
                  >
                    ⚡ Přeblokace
                  </button>
                  {showPreblokaceModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[130] p-4">
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[98vw] max-w-7xl h-[92vh] flex flex-col">
                        <div className="px-5 py-3 rounded-t-xl flex items-center justify-between flex-shrink-0 bg-cyan-700">
                          <div className="flex items-center gap-2 text-white">
                            <span className="text-lg">⚡</span>
                            <h3 className="font-bold text-base">Přeblokační formulář (PowerApps)</h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href="https://apps.powerapps.com/play/e/default-68283f3b-8487-4c86-adb3-a5228f18b893/a/32f8c771-c3ee-4c46-a691-335c98725914?tenantId=68283f3b-8487-4c86-adb3-a5228f18b893&hint=e8908d19-d616-4fd4-9f1f-272ad6ac229a&source=sharebutton&sourcetime=1781019302930"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors text-white"
                            >
                              ↗️ Otevřít v novém okně
                            </a>
                            <button onClick={() => setShowPreblokaceModal(false)} className="p-1 hover:bg-white/20 rounded transition text-white">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-900 rounded-b-xl overflow-hidden">
                          <iframe
                            src="https://apps.powerapps.com/play/e/default-68283f3b-8487-4c86-adb3-a5228f18b893/a/32f8c771-c3ee-4c46-a691-335c98725914?tenantId=68283f3b-8487-4c86-adb3-a5228f18b893&hint=e8908d19-d616-4fd4-9f1f-272ad6ac229a&source=sharebutton&sourcetime=1781019302930"
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title="Přeblokační formulář"
                            allow="fullscreen"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Filtry stavů */}
                  <button
                    className={`px-4 py-2 rounded font-semibold transition ${
                      showToday 
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200'
                    }`}
                    onClick={() => { const v = !showToday; setShowToday(v); localStorage.setItem('filterShowToday', v); }}
                  >
                    📅 Dnes ({plannedToday})
                  </button>
                  
                  <button
                    className={`px-4 py-2 rounded font-semibold transition ${
                      showOverdue 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200'
                    }`}
                    onClick={() => { const v = !showOverdue; setShowOverdue(v); localStorage.setItem('filterShowOverdue', v); }}
                  >
                    🔥 Propadlé ({overdueCount})
                  </button>
                  
                  {/* Sjednocený panel filtrů */}
                  <div className="relative filters-panel-container">
                    <button
                      className={`px-4 py-2 rounded font-semibold transition ${
                        statusFilter.length > 0 || forecastFilter.length > 0 || operatorFilter.length > 0 || bmslMin || bmslMax || prmFilter || leadFilter
                          ? 'bg-purple-600 hover:bg-purple-700 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200'
                      }`}
                      onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                    >
                      🔍 Filtry {(statusFilter.length + forecastFilter.length + operatorFilter.length + (bmslMin ? 1 : 0) + (bmslMax ? 1 : 0) + (prmFilter ? 1 : 0) + (leadFilter ? 1 : 0)) > 0 && `(${statusFilter.length + forecastFilter.length + operatorFilter.length + (bmslMin ? 1 : 0) + (bmslMax ? 1 : 0) + (prmFilter ? 1 : 0) + (leadFilter ? 1 : 0)})` }
                    </button>

                    {showFiltersPanel && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl z-50 w-[860px] p-4">
                        {/* Hlavička s řazením vpravo */}
                        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Filtry</span>
                            {activeTemplate && (
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${templateIsDirty ? 'bg-orange-100 border-orange-300 text-orange-700 dark:bg-orange-900/40 dark:border-orange-600 dark:text-orange-300' : 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-600 dark:text-blue-300'}`}>
                                {templateIsDirty ? `✏️ ${activeTemplate.name} (upraveno)` : `📌 aktivní: ${activeTemplate.name}`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Řadit:</span>
                            <button
                              onClick={() => setMainSortOrder('date')}
                              className={`text-xs px-2 py-1 rounded transition ${mainSortOrder === 'date' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-500'}`}
                            >
                              📅 Datum akce
                            </button>
                            <button
                              onClick={() => setMainSortOrder('bmsl_asc')}
                              className={`text-xs px-2 py-1 rounded transition ${mainSortOrder === 'bmsl_asc' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-500'}`}
                            >
                              BMSL ↑
                            </button>
                            <button
                              onClick={() => setMainSortOrder('bmsl_desc')}
                              className={`text-xs px-2 py-1 rounded transition ${mainSortOrder === 'bmsl_desc' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-500'}`}
                            >
                              BMSL ↓
                            </button>
                            <button
                              onClick={() => {
                                setStatusFilter([]);
                                setForecastFilter([]);
                                setOperatorFilter([]);
                                setBmslMin("");
                                setBmslMax("");
                                setPrmFilter("");
                                setLeadFilter(false);
                                setShowToday(false);
                                setShowOverdue(false);
                                localStorage.removeItem('filterShowToday');
                                localStorage.removeItem('filterShowOverdue');
                                setActiveTemplateId(null);
                                localStorage.removeItem('activeFilterTemplateId');
                              }}
                              className="text-xs px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                              title="Zrušit všechny filtry"
                            >
                              ✕ Odznačit vše
                            </button>
                            {activeTemplateId && templateIsDirty && (
                              <button
                                onClick={updateFilterTemplate}
                                className="text-xs px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded transition"
                                title={`Aktualizovat filtry uložené v šabloně „${activeTemplate?.name}"`}
                              >
                                🔄 Aktualizovat šablonu
                              </button>
                            )}
                            <button
                              onClick={() => { setShowSaveTemplateInput(v => !v); setNewTemplateName(''); }}
                              className="text-xs px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition"
                              title="Uložit aktuální filtry jako pojmenovanou šablonu"
                            >
                              💾 Uložit jako šablonu
                            </button>
                          </div>
                        </div>

                        {/* Inline formulář pro uložení šablony */}
                        {showSaveTemplateInput && (
                          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                            <input
                              type="text"
                              autoFocus
                              placeholder="Název šablony..."
                              value={newTemplateName}
                              onChange={e => setNewTemplateName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveFilterTemplate(); if (e.key === 'Escape') { setShowSaveTemplateInput(false); setNewTemplateName(''); } }}
                              className={`flex-1 px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                            />
                            <button
                              onClick={saveFilterTemplate}
                              disabled={!newTemplateName.trim()}
                              className="text-xs px-3 py-1 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded transition"
                            >
                              Uložit
                            </button>
                            <button
                              onClick={() => { setShowSaveTemplateInput(false); setNewTemplateName(''); }}
                              className="text-xs px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white rounded transition"
                            >
                              ✕
                            </button>
                          </div>
                        )}

                        {/* Uložené šablony */}
                        {filterTemplates.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                            <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Šablony:</span>
                            {filterTemplates.map(tpl => (
                              <div key={tpl.id} className="flex items-center gap-0.5">
                                <button
                                  onClick={() => applyFilterTemplate(tpl)}
                                  className={`text-xs px-2 py-1 border rounded-l transition ${
                                    activeTemplateId === tpl.id
                                      ? 'bg-blue-600 text-white border-blue-700 dark:bg-blue-500 dark:border-blue-600'
                                      : 'bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-800/60 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                                  }`}
                                  title={`Aplikovat šablonu „${tpl.name}"`}
                                >
                                  {tpl.name}
                                </button>
                                <button
                                  onClick={() => deleteFilterTemplate(tpl.id)}
                                  className="text-xs px-1.5 py-1 bg-blue-100 dark:bg-blue-900/40 hover:bg-red-100 dark:hover:bg-red-900/40 text-blue-500 hover:text-red-600 dark:text-blue-400 dark:hover:text-red-400 border border-l-0 border-blue-300 dark:border-blue-700 hover:border-red-300 dark:hover:border-red-700 rounded-r transition"
                                  title={`Smazat šablonu „${tpl.name}"`}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Mřížka filtrů – 6 sloupců, Stav zabírá 2 */}
                        <div className="grid grid-cols-6 gap-0 divide-x divide-gray-200 dark:divide-gray-600">
                          {/* Filtr stavů – 2 sloupce */}
                          <div className="col-span-2 pr-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Stav</span>
                              {statusFilter.length > 0 && (
                                <button onClick={() => setStatusFilter([])} className="text-xs text-red-500 hover:text-red-700">✕ ({statusFilter.length})</button>
                              )}
                            </div>
                            <input
                              type="text"
                              placeholder="Hledat stav..."
                              value={statusSearch}
                              onChange={e => setStatusSearch(e.target.value)}
                              className={`w-full px-2 py-1 text-xs border rounded mb-1 ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                            />
                            <div className="max-h-[32rem] overflow-y-auto space-y-1">
                              {uniqueStatuses.filter(s => !statusSearch || s.toLowerCase().includes(statusSearch.toLowerCase())).map(status => (
                                <label key={status} className="flex items-center gap-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                                  <input type="checkbox" checked={statusFilter.includes(status)} onChange={() => toggleStatusFilter(status)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                  <span className="text-xs text-gray-900 dark:text-white">{getStatusEmoji(status)} {status}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Filtr FC */}
                          <div className="px-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Forecast</span>
                              {forecastFilter.length > 0 && (
                                <button onClick={() => setForecastFilter([])} className="text-xs text-red-500 hover:text-red-700">✕ ({forecastFilter.length})</button>
                              )}
                            </div>
                            <div className="max-h-[36rem] overflow-y-auto space-y-1">
                              {uniqueForecasts.map(forecast => (
                                <label key={forecast} className="flex items-center gap-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                                  <input type="checkbox" checked={forecastFilter.includes(forecast)} onChange={() => toggleForecastFilter(forecast)} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                                  <span className="text-xs text-gray-900 dark:text-white">📊 {forecast}%</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Filtr Operátor */}
                          <div className="px-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Operátor</span>
                              {operatorFilter.length > 0 && (
                                <button onClick={() => setOperatorFilter([])} className="text-xs text-red-500 hover:text-red-700">✕ ({operatorFilter.length})</button>
                              )}
                            </div>
                            <div className="max-h-[36rem] overflow-y-auto space-y-1">
                              {uniqueOperators.map(op => (
                                <label key={op} className="flex items-center gap-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                                  <input type="checkbox" checked={operatorFilter.includes(op)} onChange={() => toggleOperatorFilter(op)} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                                  <span className="text-xs text-gray-900 dark:text-white">{op}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Filtr BMSL */}
                          <div className="px-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">BMSL</span>
                              {(bmslMin || bmslMax) && (
                                <button onClick={() => { setBmslMin(""); setBmslMax(""); }} className="text-xs text-red-500 hover:text-red-700">✕</button>
                              )}
                            </div>
                            <input
                              type="number"
                              placeholder="Vyšší než..."
                              value={bmslMin}
                              onChange={e => setBmslMin(e.target.value)}
                              className={`w-full px-2 py-1 text-xs border rounded mb-2 ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                            />
                            <input
                              type="number"
                              placeholder="Nižší než..."
                              value={bmslMax}
                              onChange={e => setBmslMax(e.target.value)}
                              className={`w-full px-2 py-1 text-xs border rounded ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                            />
                          </div>

                          {/* Filtr PRM */}
                          <div className="pl-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">PRM</span>
                              {prmFilter && (
                                <button onClick={() => setPrmFilter("")} className="text-xs text-red-500 hover:text-red-700">✕</button>
                              )}
                            </div>
                            <input
                              type="text"
                              placeholder="Obsahuje..."
                              value={prmFilter}
                              onChange={e => setPrmFilter(e.target.value)}
                              className={`w-full px-2 py-1 text-xs border rounded mb-2 ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                            />
                            {/* Filtr leady */}
                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">Leady</span>
                              <label className="flex items-center gap-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                                <input
                                  type="checkbox"
                                  checked={leadFilter}
                                  onChange={() => setLeadFilter(!leadFilter)}
                                  className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-xs text-gray-900 dark:text-white">🏷 Pouze leady</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 relative min-w-[200px]">
                    <input
                      type="text"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-ellipsis"
                      placeholder="Filtrujte podle názvu, IČO, kontaktu..."
                      value={search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    {search && (
                      <button
                        onClick={() => handleSearchChange('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl w-8 h-8 flex items-center justify-center"
                        title="Vymazat vyhledávání"
                      >
                        ❌
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobilní vyhledávání - ve fixní horní části */}
              <div className="lg:hidden mb-2 flex-shrink-0">
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg relative">
                  <input
                    type="text"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-base min-h-[50px]"
                    placeholder="Filtrujte podle názvu, IČO, kontaktu..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{
                      fontSize: '16px' // Prevence iOS zoom
                    }}
                  />
                  {search && (
                    <button
                      onClick={() => handleSearchChange('')}
                      className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl w-10 h-10 flex items-center justify-center"
                      title="Vymazat vyhledávání"
                    >
                      ❌
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Hlavní scrollovatelná oblast - pouze seznam firem */}
            <div className="flex-1 min-h-0 w-[98vw] max-w-[1800px] mx-auto px-1 flex flex-col">

              {/* Loading indikátor pro globální vyhledávání */}
              {isSearchingGlobally && userRole === 'admin' && (
                <div className="mb-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg text-blue-800 dark:text-blue-200 text-sm font-medium flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  🔍 Vyhledávám globálně napříč všemi firmami...
                </div>
              )}

              {/* Informace o globálním vyhledávání */}
              {isGlobalSearch && userRole === 'admin' && !isSearchingGlobally && (
                <div className="mb-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-800 dark:text-red-200 text-sm font-medium">
                  🌍 Globální vyhledávání napříč všemi firmami v CRM • Nalezeno: {globalSearchResults.length} firem
                </div>
              )}

              {/* Seznam společností - Desktop tabulka s scrollem */}
              <div className="hidden lg:block flex-1 min-h-0 overflow-hidden">
                <div className={`rounded-lg shadow-lg border h-full overflow-hidden transition-colors ${
                  isGlobalSearch && userRole === 'admin' 
                    ? 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-700' 
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="h-full overflow-y-auto custom-scrollbar">
                    <table className="min-w-full table-fixed">
                      <thead className="sticky top-0 z-10">
                        <tr className={`text-blue-900 dark:text-gray-100 ${
                          isGlobalSearch && userRole === 'admin'
                            ? 'bg-gradient-to-r from-red-100 to-red-200 dark:from-red-900/40 dark:to-red-800/40'
                            : 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600'
                        }`}>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[12%] font-semibold text-sm">Datum další akce</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[17%] font-semibold text-sm">Název firmy</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[6%] font-semibold text-sm">IČO</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[6%] font-semibold text-sm">Operátor</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[5%] font-semibold text-sm">BMSL</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[4%] font-semibold text-sm">FC</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[10%] font-semibold text-sm">Očekávaná provize</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[8%] font-semibold text-sm">PRM</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[10%] font-semibold text-sm">Stav</th>
                          <th className="py-3 px-4 text-left whitespace-nowrap w-[17%] font-semibold text-sm">{isGlobalSearch && userRole === 'admin' ? 'Vlastník' : 'Aktivita'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                      {pagedCompanies.length === 0 && (
                        <tr>
                          <td colSpan={10} className="text-gray-500 dark:text-gray-400 py-8 text-center">
                            <div className="flex flex-col items-center">
                              <div className="text-4xl mb-2">📋</div>
                              <div>Žádné společnosti</div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {pagedCompanies.map((company) => {
                        const lastActivity = (company.activities || []).slice(-1)[0];
                        const isOverdueCompany = isOverdue(company.nextActionDate);
                        const isTodayCompany = isToday(company.nextActionDate);
                        
                        // Výpočet očekávané provize
                        const calculateExpectedCommission = (bmsl) => {
                          if (!bmsl || bmsl === "") return "";
                          const bmslValue = parseFloat(bmsl);
                          if (isNaN(bmslValue) || bmslValue <= 0) return "";
                          const cMin = commissionCoef?.coefMin ?? 4;
                          const cMax = commissionCoef?.coefMax;
                          if (cMax == null) {
                            return `${Math.round(bmslValue * cMin).toLocaleString()} Kč`;
                          }
                          const lowerBound = Math.round(bmslValue * cMin);
                          const upperBound = Math.round(bmslValue * cMax);
                          return `${lowerBound.toLocaleString()} - ${upperBound.toLocaleString()} Kč`;
                        };
                        
                        let rowClass = "hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all duration-200 text-gray-900 dark:text-white";
                        if (isOverdueCompany) rowClass += " bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/30 dark:to-pink-900/30";
                        if (isTodayCompany) rowClass += " bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/30 dark:to-amber-900/30";

                        return (
                          <tr 
                            key={company._id} 
                            className={`${rowClass} cursor-pointer`}
                            onClick={() => navigate(`/companies/${company._id}`)}
                          >
                            <td className="py-2 px-4 w-[12%] max-w-0">
                              <div className="flex items-center gap-2 truncate">
                                {isOverdueCompany && <span title="Propadlé" className="text-lg flex-shrink-0">🔥</span>}
                                {isTodayCompany && <span title="Dnes" className="text-lg flex-shrink-0">📅</span>}
                                <div className="text-sm font-medium min-w-0">
                                  {company.nextActionDate ? (
                                    <div className="truncate">
                                      <span>{new Date(company.nextActionDate).toLocaleDateString('cs-CZ')}</span>
                                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">{new Date(company.nextActionDate).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                  ) : ""}
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-4 w-[17%] max-w-0 truncate font-medium" title={company.name}>
                              {company.name}
                            </td>
                            <td className="py-2 px-4 w-[6%] max-w-0 truncate text-sm text-gray-600 dark:text-gray-300">{company.ico}</td>
                            <td className="py-2 px-4 w-[6%] max-w-0 truncate text-sm">{getOperatorDisplayText(company.operator)}</td>
                            <td className="py-2 px-4 w-[5%] max-w-0 truncate text-sm font-medium text-green-600 dark:text-green-400">{company.bmsl || ""}</td>
                            <td className="py-2 px-4 w-[4%] max-w-0 truncate text-sm font-medium text-blue-600 dark:text-blue-400">{company.forecast ? `${company.forecast}%` : "0%"}</td>
                            <td className="py-2 px-4 w-[10%] max-w-0 truncate text-sm text-gray-700 dark:text-gray-300" title={`Očekávaná provize podle BMSL: ${company.bmsl || 'N/A'}`}>
                              {calculateExpectedCommission(company.bmsl)}
                            </td>
                            <td className="py-2 px-4 w-[8%] max-w-0 truncate text-sm text-gray-600 dark:text-gray-300">{company.prm || ""}</td>
                            <td className="py-2 px-4 w-[10%] max-w-0 truncate text-sm" title={company.status}>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                {company.status}
                              </span>
                            </td>
                            <td className="py-2 px-4 w-[17%] max-w-0 truncate text-sm text-gray-600 dark:text-gray-300" title={isGlobalSearch && userRole === 'admin' ? (company.createdBy?.name || 'Neznámý') : (lastActivity ? lastActivity.text : "")}>
                              {isGlobalSearch && userRole === 'admin' ? (company.createdBy?.name || 'Neznámý') : (lastActivity ? lastActivity.text : "")}
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Seznam společností - Mobile karty se scrollem */}
              <div className={`lg:hidden space-y-4 overflow-y-auto flex-1 pb-4 rounded-lg p-2 transition-colors ${
                isGlobalSearch && userRole === 'admin'
                  ? 'bg-red-50 dark:bg-red-900/10'
                  : ''
              }`}>
                {pagedCompanies.length === 0 && (
                  <div className="text-gray-500 dark:text-gray-400 py-8 text-center">
                    <div className="text-4xl mb-2">📋</div>
                    <div>Žádné společnosti</div>
                  </div>
                )}
                {pagedCompanies.map((company) => {
                  const lastActivity = (company.activities || []).slice(-1)[0];
                  const isOverdueCompany = isOverdue(company.nextActionDate);
                  const isTodayCompany = isToday(company.nextActionDate);
                  
                  // Výpočet očekávané provize pro mobilní karty
                  const calculateExpectedCommissionMobile = (bmsl) => {
                    if (!bmsl || bmsl === "") return "";
                    const bmslValue = parseFloat(bmsl);
                    if (isNaN(bmslValue) || bmslValue <= 0) return "";
                    const cMin = commissionCoef?.coefMin ?? 4;
                    const cMax = commissionCoef?.coefMax;
                    if (cMax == null) {
                      return `${Math.round(bmslValue * cMin).toLocaleString()} Kč`;
                    }
                    const lowerBound = Math.round(bmslValue * cMin);
                    const upperBound = Math.round(bmslValue * cMax);
                    return `${lowerBound.toLocaleString()}-${upperBound.toLocaleString()} Kč`;
                  };
                  
                  let cardClass = "bg-white dark:bg-gray-800 rounded-lg shadow-md border-l-4 border border-gray-200 dark:border-gray-700 p-4 transition-all";
                  if (isOverdueCompany) cardClass += " border-l-red-500 bg-red-50 dark:bg-red-900/20";
                  else if (isTodayCompany) cardClass += " border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20";
                  else cardClass += " border-l-blue-500";

                  return (
                    <div key={company._id} className={cardClass}>
                      {/* Optimalizovaný dvousloupcový layout */}
                      <div className="flex gap-2 mb-3">
                        {/* LEVÁ strana - hlavní informace - zúženo */}
                        <div className="flex-1 min-w-0 max-w-[60%]">
                          <h3 className="font-semibold text-base text-gray-900 dark:text-white truncate mb-1">
                            {company.name}
                          </h3>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            IČO: {company.ico}
                          </div>
                          {isGlobalSearch && userRole === 'admin' ? (
                            <div className="bg-blue-50 dark:bg-blue-900/30 rounded p-1.5">
                              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">Vlastník:</div>
                              <div className="text-xs text-gray-900 dark:text-white font-semibold">{company.createdBy?.name || 'Neznámý'}</div>
                            </div>
                          ) : lastActivity && (
                            <div className="bg-gray-50 dark:bg-gray-700 rounded p-1.5">
                              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">Poslední aktivita:</div>
                              <div className="text-xs text-gray-900 dark:text-white line-clamp-2 leading-tight">{lastActivity.text}</div>
                            </div>
                          )}
                        </div>

                        {/* PRAVÁ strana - status a akce v menším písmu, rozšířeno */}
                        <div className="flex-shrink-0 w-40 text-right space-y-1">
                          {company.status && (
                            <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded text-xs font-medium leading-tight">
                              {company.status}
                            </div>
                          )}
                          
                          {company.nextActionDate && (
                            <div className="text-xs text-gray-900 dark:text-white leading-tight">
                              <div className="flex items-center justify-end gap-1">
                                <span>{new Date(company.nextActionDate).toLocaleDateString('cs-CZ')} {new Date(company.nextActionDate).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}</span>
                                {isOverdueCompany && <span title="Propadlé" className="text-xs">🔥</span>}
                                {isTodayCompany && <span title="Dnes" className="text-xs">📅</span>}
                              </div>
                            </div>
                          )}
                          
                          {company.operator && (
                            <div className="text-xs text-gray-900 dark:text-white leading-tight">{getOperatorDisplayText(company.operator)}</div>
                          )}
                          
                          {company.bmsl && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 leading-tight">BMSL: {company.bmsl}</div>
                          )}
                          
                          {(company.forecast || company.forecast === 0) ? (
                            <div className="text-xs text-gray-600 dark:text-gray-400 leading-tight">FC: {company.forecast}%</div>
                          ) : (
                            <div className="text-xs text-gray-600 dark:text-gray-400 leading-tight">FC: 0%</div>
                          )}
                          
                          {company.bmsl && calculateExpectedCommissionMobile(company.bmsl) && (
                            <div className="text-xs text-gray-700 dark:text-gray-300 leading-tight" title="Očekávaná provize">
                              {calculateExpectedCommissionMobile(company.bmsl)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Akční tlačítka - kompaktní */}
                      <div className="flex gap-2">
                        <button
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-2 rounded text-sm font-medium transition-colors"
                          onClick={() => navigate(`/companies/${company._id}`)}
                        >
                          📝 Detail
                        </button>
                        {/* Tlačítko pro volání - zelené se sluchátkem */}
                        {company.contacts && company.contacts.length > 0 && company.contacts[0].phone && (
                          <>
                            <button
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-1.5 px-2 rounded text-sm font-medium transition-colors"
                              onClick={() => {
                                const phoneNumber = company.contacts[0].phone.replace(/\s/g, '');
                                window.open(`tel:${phoneNumber}`, '_self');
                              }}
                              title={`Zavolat: ${company.contacts[0].phone}`}
                            >
                              📞 Volat
                            </button>
                            {/* Tlačítko pr SMS - žluté s ikonkou zprávy */}
                            <button
                              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-1.5 px-2 rounded text-sm font-medium transition-colors"
                              onClick={() => {
                                const phoneNumber = company.contacts[0].phone.replace(/\s/g, '');
                                window.open(`sms:${phoneNumber}`, '_self');
                              }}
                              title={`Poslat SMS: ${company.contacts[0].phone}`}
                            >
                              💬 SMS
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
            
            {/* Ovládání stránek - fixní dole na mobilu, normální na desktopu */}
            <div className="flex-shrink-0 w-[98vw] max-w-[1800px] mx-auto px-2 pb-4">
              <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 lg:p-4 shadow lg:mt-4 mb-8 lg:mb-4">
                {/* Levá strana – widget výkon obchodníků + duel pozvánka (pouze desktop) */}
                <div className="hidden lg:flex items-center gap-2 flex-shrink-0 overflow-visible">
                  {userId && currentUser && (
                    <UserStatsWidget currentUser={currentUser} userRole={userRole} isDarkMode={isDarkMode} />
                  )}
                  {userId && currentUser && (
                    <DuelBar currentUser={currentUser} isDarkMode={isDarkMode} />
                  )}
                </div>
                
                {/* Střed - tlačítka pro stránkování - mobilní centered */}
                <div className="flex items-center gap-2 flex-1 lg:flex-initial justify-center">
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    ← Předchozí
                  </button>
                  <span className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white bg-blue-100 dark:bg-blue-900 rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Další →
                  </button>
                  <span className="hidden lg:inline text-sm text-gray-600 dark:text-gray-400 ml-2">
                    Zobrazeno {Math.min(startIndex + 1, sortedCompanies.length)}-{Math.min(startIndex + itemsPerPage, sortedCompanies.length)} z {sortedCompanies.length}
                  </span>
                </div>
                
                {/* Pravá strana - prázdný spacer pro symetrii (pouze desktop) */}
                <div className="hidden lg:flex items-center flex-shrink-0">
                  <div className="w-4" />
                </div>
              </div>
            </div>
          </div>
        } />
        <Route 
          path="/companies/new" 
          element={
            <NewCompanyPage 
              users={users} 
              handleSaveNewCompany={handleSaveNewCompany}
              isDarkMode={isDarkMode}
              companies={companies}
              userRole={userRole}
              fetchCompanies={fetchCompanies}
              userId={userId}
              onOpenMeetingPlanner={handleOpenMeetingPlannerWithCustomer}
            />
          } 
        />
        <Route 
          path="/companies/:id" 
          element={
            <div style={{ paddingTop: isIOSPWA ? 'env(safe-area-inset-top)' : undefined }}>
            <CompanyDetailPage 
              companies={companies} 
              filteredCompanies={filteredCompanies}
              fetchCompanies={(id) => id && fetchCompanies(id)} 
              userId={userId}
              isDarkMode={isDarkMode}
              userRole={userRole}
              onOpenMeetingPlanner={handleOpenMeetingPlannerWithCustomer}
              users={users}
              setCurrentOpenCompanyId={setCurrentOpenCompanyId}
            />
            </div>
          } 
        />
        <Route path="/dashboard" element={<Dashboard companies={companies} users={users} currentUserId={userId} userRole={userRole} isDarkMode={isDarkMode} fetchCompanies={fetchCompanies} />} />
        <Route path="/account" element={<MyAccount userId={userId} isDarkMode={isDarkMode} />} />
        <Route path="/contacts" element={<Contacts userRole={userRole} currentUserId={userId} isDarkMode={isDarkMode} />} />
        <Route path="/sprava" element={<Sprava userId={userId} userRole={userRole} isDarkMode={isDarkMode} />} />
        <Route path="/cloud" element={<CloudStorage currentUserId={userId} userRole={userRole} isDarkMode={isDarkMode} />} />
        <Route path="/users" element={userRole === 'admin' ? <UsersAdmin isDarkMode={isDarkMode} userRole={userRole} currentUserId={userId} companies={companies} /> : <Navigate to="/" replace />} />
        <Route path="/status-editor" element={<StatusEditor isDarkMode={isDarkMode} onClose={() => navigate('/users')} isModal={false} currentUserId={userId} userRole={userRole} />} />
        <Route path="/batch-visibility" element={<BatchVisibilityManager isDarkMode={isDarkMode} userRole={userRole} currentUserId={userId} />} />
        <Route path="/contact-management" element={userRole === 'admin' ? <ContactManagement isDarkMode={isDarkMode} userRole={userRole} currentUserId={userId} fetchCompanies={fetchCompanies} companies={companies} /> : <Navigate to="/" replace />} />
        <Route path="/bulk-assignment" element={userRole === 'admin' ? <BulkAssignment isDarkMode={isDarkMode} currentUserId={userId} /> : <Navigate to="/" replace />} />
        <Route path="/duplicate-checker" element={userRole === 'admin' ? <DuplicateChecker isDarkMode={isDarkMode} /> : <Navigate to="/" replace />} />
        <Route path="/commissions" element={<CommissionsWithTabs userRole={userRole} users={users} companies={companies} currentUserId={userId} isDarkMode={isDarkMode} />} />
        <Route path="/requests" element={<Requests currentUserId={userId} userRole={userRole} isDarkMode={isDarkMode} users={users} companies={companies} />} />
        <Route path="/activities" element={<Activities companies={companies} users={users} currentUserId={userId} userRole={userRole} isDarkMode={isDarkMode} />} />
        <Route path="/backup" element={<BackupRestore companies={companies} users={users} userId={userId} isAdmin={userRole === "admin"} onRestore={() => userId && fetchCompanies(userId)} isDarkMode={isDarkMode} />} />
        <Route path="/restore-backup" element={userRole === 'admin' ? <RestoreFromBackup userId={userId} isDarkMode={isDarkMode} onRestore={() => userId && fetchCompanies(userId)} /> : <Navigate to="/" replace />} />
        <Route path="/knowledge" element={<MindMapKnowledgeManager isDarkMode={isDarkMode} onClose={() => navigate('/users')} userRole={userRole} userId={userId} />} />
        <Route path="/callcenter-admin" element={<CallCenterAdmin isDarkMode={isDarkMode} userRole={userRole} />} />
        <Route path="/welcome-admin" element={<WelcomeAdmin isDarkMode={isDarkMode} userId={userId} />} />
        <Route path="/discord/callback" element={<DiscordCallback />} />
        <Route path="/phone-sync" element={<MobilePhoneSync userId={userId} isDarkMode={isDarkMode} />} />
        <Route path="/suppliers" element={userRole === 'admin' ? <SuppliersAdmin userRole={userRole} currentUserId={userId} isDarkMode={isDarkMode} /> : <Navigate to="/" replace />} />
        <Route path="/superusery" element={userRole === 'admin' ? <SuperuseryAdmin isDarkMode={isDarkMode} /> : <Navigate to="/" replace />} />
        <Route path="/uvolneni-mustr" element={userRole === 'admin' ? <UvolneniMustrAdmin isDarkMode={isDarkMode} /> : <Navigate to="/" replace />} />
        <Route path="/backoffice" element={userRole === 'admin' ? <BackofficeAdmin isDarkMode={isDarkMode} userRole={userRole} currentUserId={userId} /> : <Navigate to="/" replace />} />
        <Route path="/lead-ba" element={userRole === 'admin' ? <LeadBaAdmin isDarkMode={isDarkMode} userRole={userRole} currentUserId={userId} /> : <Navigate to="/" replace />} />
        <Route path="/leadtool" element={<LeadtoolLogin />} />
        <Route path="/leadtool/register" element={<LeadtoolRegister />} />
        <Route path="/leadtool/dashboard" element={<LeadtoolDashboard />} />
      </Routes>
      
      {/* Meeting Planner Modal */}
      {showMeetingPlanner && (
        <MeetingPlanner
          companies={companies}
          userId={userId}
          isDarkMode={isDarkMode}
          onClose={handleCloseMeetingPlanner}
          onRefreshCompanies={() => fetchCompanies(userId)}
          preselectedCustomer={selectedCustomerForPlanner}
          statusFilter={statusFilter}
          uniqueStatuses={uniqueStatuses}
          toggleStatusFilter={toggleStatusFilter}
          getStatusEmoji={getStatusEmoji}
        />
      )}
      

      
      {/* Chat komponenta */}
      {userId && users.length > 0 && isMainPage && (
        <Chat currentUserId={userId} users={users} isAdmin={userRole === "admin"} userRole={userRole} />
      )}
      
      {/* Notifikace vedle chatu */}
      {userId && currentUser && (isMainPage || location.pathname === '/requests') && (
        <NotificationBell currentUser={currentUser} />
      )}
      
      {/* Synchronizace s telefonem - ikona vedle notifikací, běží vždy pro heartbeat a sync firmy */}
      {userId && (
        <PhoneSync userId={userId} currentCompanyId={currentOpenCompanyId} hideIcon={!isMainPage} />
      )}

      {/* Statistika leadů - callcentrum modal */}
      {showCallCenterStats && (
        <CallCenterStatsModal
          onClose={() => setShowCallCenterStats(false)}
          isDarkMode={isDarkMode}
          currentUserId={userId}
        />
      )}

      {/* Denní uvítací zpráva / citát */}
      {dailyPopup.show && (
        <DailyMessagePopup
          message={dailyPopup.message}
          userId={userId}
          onDismiss={handleDismissPopup}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Automatické ověření na pozadí — plovoucí lišta (viditelná z celé aplikace) */}
      <VerificationStatusBar currentUserId={userId} isDarkMode={isDarkMode} />
    </div>
    </VerificationProvider>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <MainApp />
    </Router>
  );
}

function calculateWorkingDays(startDate, days) {
  const result = [];
  let currentDate = new Date(startDate);
  while (result.length < days) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude Sundays (0) and Saturdays (6)
      result.push(new Date(currentDate));
    }
  }
  return result;
}

function getActivitiesForDays(companies, days) {
  return days.map(day => {
    return companies.filter(company => {
      if (!company.nextActionDate) return false;
      const actionDate = new Date(company.nextActionDate);
      return isSameDay(actionDate, day);
    }).length;
  });
}
